import React, { useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
// FaTimes icon needed for the modal close button
import {
  FaUsers,
  FaSuitcase,
  FaSnowflake,
  FaCar,
  FaTaxi,
  FaRupeeSign,
  FaMapMarkerAlt,
  FaArrowRight,
  FaCheck,
  FaCheckCircle,
  FaCalendarAlt,
  FaClock,
  FaEdit,
  FaTimes,
  FaPlane,
  FaGift,
  FaChevronUp,
  FaChevronDown,
  FaEye,
  FaRoute,
  FaArrowDown,
  FaSort,
  FaFilter,
  FaChair,
  FaSearch,
} from "react-icons/fa";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import { checkFixedRoute } from "../services/routesService";
import { calculateCabPrice } from "../utils/priceCalculator";
import { getDistanceBetweenCities } from "../utils/distanceCalculator";
import { extractCityAndState } from "../utils/locationMatcher";
import { API_URL } from "../config/apiConfig";
import TripDetailsSummary from "../components/TripDetailsSummary";
import BenefitsBanner from "../components/BenefitsBanner";
import CabCard from "../components/CabCard";
import { trackAbandonedBooking } from "../utils/abandonedBookingTracker";

const POPULAR_CITIES = [
  { city: "Rajkot", state: "Gujarat, India" },
  { city: "Bangalore", state: "Karnataka, India" },
  { city: "Pune", state: "Maharashtra, India" },
  { city: "Mumbai", state: "Maharashtra, India" },
  { city: "Ahmedabad", state: "Gujarat, India" },
  { city: "Delhi", state: "Delhi, India" },
  { city: "Surat", state: "Gujarat, India" },
  { city: "Vadodara", state: "Gujarat, India" },
  { city: "Jaipur", state: "Rajasthan, India" },
  { city: "Hyderabad", state: "Telangana, India" },
  { city: "Goa", state: "Goa, India" },
  { city: "Chennai", state: "Tamil Nadu, India" },
];

// Utility function to format date in DD-MM-YYYY format
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "Not set";

  try {
    // If it's a YYYY-MM-DD string, format it directly to avoid timezone shift
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    return "Invalid date";
  }
};

// Utility function to format date in Goibibo format: "Thu, 05 Nov 2026"
const formatGoibiboDate = (dateString) => {
  if (!dateString) return "Not set";
  try {
    let d;
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    } else {
      d = new Date(dateString);
    }
    if (isNaN(d.getTime())) return "Invalid date";
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayName = days[d.getDay()];
    const day = String(d.getDate()).padStart(2, "0");
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${dayName}, ${day} ${month} ${year}`;
  } catch (error) {
    return dateString;
  }
};

// Compact date & time formatter: "04 Nov, 12:30 PM"
const formatGoibiboShortDateTime = (dateString, timeString) => {
  if (!dateString) return "Not set";
  try {
    let d;
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    } else {
      d = new Date(dateString);
    }
    if (isNaN(d.getTime())) return `${dateString} • ${formatTime12Hour(timeString)}`;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = String(d.getDate()).padStart(2, "0");
    const month = months[d.getMonth()];
    const timeFormatted = formatTime12Hour(timeString);
    return `${day} ${month}, ${timeFormatted}`;
  } catch (error) {
    return `${dateString} • ${formatTime12Hour(timeString)}`;
  }
};

// Utility function to check if a string is raw GPS coordinates (e.g. "26.1196607,85.390982")
const isCoordinates = (str) => {
  if (!str || typeof str !== "string") return false;
  return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(str.trim());
};

// Utility function to resolve readable display name for location (preferring city/place name over raw lat/lng)
const getDisplayLocation = (loc, fallbackCity) => {
  if (loc && !isCoordinates(loc)) return loc;
  if (fallbackCity && !isCoordinates(fallbackCity)) return fallbackCity;
  return loc || fallbackCity || "";
};

// Utility function to get local date string in YYYY-MM-DD format
const getLocalDateString = (dateObj) => {
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Utility function to parse a date and time string into a valid Date object safely
const parseDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return new Date(NaN);

  let cleanTime = timeStr.trim().toLowerCase();
  
  // Check if it has AM/PM
  const ampmMatch = cleanTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10);
    const minutes = ampmMatch[2];
    const modifier = ampmMatch[3];
    if (modifier === "pm" && hours < 12) hours += 12;
    if (modifier === "am" && hours === 12) hours = 0;
    cleanTime = `${hours.toString().padStart(2, "0")}:${minutes}`;
  } else {
    // Keep only HH:MM format
    const match = cleanTime.match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      cleanTime = `${match[1].padStart(2, "0")}:${match[2]}`;
    }
  }

  return new Date(`${dateStr}T${cleanTime}`);
};



// Utility function to format 24-hour time string (HH:MM) to 12-hour AM/PM format
const formatTime12Hour = (time24) => {
  if (!time24) return "Not set";
  try {
    let cleanTime = time24.trim().toLowerCase();
    
    // Check if it already has AM/PM
    if (cleanTime.includes("am") || cleanTime.includes("pm")) {
      return time24.toUpperCase();
    }
    
    const [hoursStr, minutesStr] = cleanTime.split(":");
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr;
    if (isNaN(hours)) return time24;
    
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${hours.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  } catch (e) {
    return time24;
  }
};

// Module-level distance cache to make repeat searches instant
const distanceCache = new Map();

// Helper to parse via cities from URL query param cleanly without breaking on commas in addresses
const parseViaCities = (param) => {
  if (!param) return [];
  if (typeof param !== "string") {
    if (Array.isArray(param)) {
      return param.map((s) => typeof s === "string" ? s.trim() : (s?.name || s?.address || "")).filter(Boolean);
    }
    return [];
  }
  const str = param.trim();
  if (!str) return [];
  if (str.includes("|")) {
    return str.split("|").map((s) => s.trim()).filter(Boolean);
  }
  // When there is NO pipe delimiter, the entire string is ONE single stop address (do NOT split on commas!)
  return [str];
};

// Helper to parse time string into 12-hour components
const parseTimeComponents = (timeStr) => {
  if (!timeStr) return { hour: "10", minute: "00", ampm: "AM" };
  const str = timeStr.trim().toLowerCase();
  const ampmMatch = str.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const m = ampmMatch[2].padStart(2, "0");
    let ampm = ampmMatch[3] ? ampmMatch[3].toUpperCase() : (h >= 12 ? "PM" : "AM");
    if (!ampmMatch[3]) {
      if (h >= 12) {
        if (h > 12) h -= 12;
      } else if (h === 0) {
        h = 12;
      }
    } else {
      if (h === 0) h = 12;
      else if (h > 12) h -= 12;
    }
    const hourStr = String(h).padStart(2, "0");
    return { hour: hourStr, minute: m, ampm };
  }
  return { hour: "10", minute: "00", ampm: "AM" };
};

// Helper to convert 12-hour components to 24-hour string (HH:MM)
const formatTo24Hour = (hour12, minute, ampm) => {
  let h = parseInt(hour12, 10);
  if (isNaN(h)) h = 12;
  const m = String(minute).padStart(2, "0");
  if (ampm === "PM" && h < 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m}`;
};

// Custom 12-Hour Time Picker Component (Hour, Minute, AM/PM)
const TimePicker12Hour = ({ value, onChange, label, className = "" }) => {
  const { hour, minute, ampm } = parseTimeComponents(value);

  const handleHourChange = (newHour) => {
    onChange(formatTo24Hour(newHour, minute, ampm));
  };

  const handleMinuteChange = (newMinute) => {
    onChange(formatTo24Hour(hour, newMinute, ampm));
  };

  const handleAmPmChange = (newAmPm) => {
    onChange(formatTo24Hour(hour, minute, newAmPm));
  };

  const hoursList = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const defaultMinutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
  const minutesList = defaultMinutes.includes(minute) ? defaultMinutes : [...defaultMinutes, minute].sort();

  return (
    <div className={`space-y-1 ${className}`}>
      {label && <label className="block text-xs font-semibold text-gray-700">{label}</label>}
      <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-300 rounded-lg p-1.5">
        {/* Hour Select */}
        <div className="flex-1 min-w-0">
          <select
            value={hour}
            onChange={(e) => handleHourChange(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded px-1.5 py-1 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#f26a21] cursor-pointer"
          >
            {hoursList.map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </div>

        <span className="font-bold text-gray-500 text-xs">:</span>

        {/* Minute Select */}
        <div className="flex-1 min-w-0">
          <select
            value={minute}
            onChange={(e) => handleMinuteChange(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded px-1.5 py-1 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#f26a21] cursor-pointer"
          >
            {minutesList.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* AM/PM Toggle */}
        <div className="flex rounded overflow-hidden border border-gray-300 shrink-0">
          <button
            type="button"
            onClick={() => handleAmPmChange("AM")}
            className={`px-2 py-1 text-[11px] font-bold transition-colors ${
              ampm === "AM" ? "bg-[#f26a21] text-white" : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => handleAmPmChange("PM")}
            className={`px-2 py-1 text-[11px] font-bold transition-colors ${
              ampm === "PM" ? "bg-[#f26a21] text-white" : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            PM
          </button>
        </div>
      </div>
    </div>
  );
};


const CITY_ALIASES = {
  bengaluru: "bangalore",
  bangalore: "bangalore",
  mumbai: "mumbai",
  bombay: "mumbai",
  kolkata: "kolkata",
  calcutta: "kolkata",
  chennai: "chennai",
  madras: "chennai",
  delhi: "delhi",
  newdelhi: "delhi",
};

const normalizeCityNameKey = (city) => {
  if (!city) return "";
  const clean = city.toLowerCase().replace(/[^a-z0-9]/g, "").trim();
  return CITY_ALIASES[clean] || clean;
};

const extractCleanCityName = (locationStr) => {
  if (!locationStr) return "";
  const parsed = extractCityAndState(locationStr);
  if (parsed.city) return parsed.city;
  if (parsed.state && ["delhi", "goa", "chandigarh"].includes(parsed.state.toLowerCase())) {
    return parsed.state;
  }
  const parts = locationStr.split(",").map((p) => p.trim());
  return parts[0] || "";
};

export const isSameCityRoute = (fromLoc, toLoc) => {
  if (!fromLoc || !toLoc) return false;
  const fLower = fromLoc.toLowerCase().trim();
  const tLower = toLoc.toLowerCase().trim();
  if (fLower === tLower) return true;

  const c1 = extractCleanCityName(fromLoc);
  const c2 = extractCleanCityName(toLoc);

  const n1 = normalizeCityNameKey(c1);
  const n2 = normalizeCityNameKey(c2);

  if (n1 && n2 && n1 === n2) return true;

  const checkToken = (cityToken, fullStr) => {
    if (!cityToken || cityToken.length < 3) return false;
    const cleanToken = normalizeCityNameKey(cityToken);
    const parts = fullStr.split(",").map((p) => p.trim().toLowerCase());
    return parts.some((p) => {
      const cleanP = normalizeCityNameKey(p);
      return cleanP === cleanToken || p.includes(cityToken.toLowerCase());
    });
  };

  if (c1 && checkToken(c1, toLoc)) return true;
  if (c2 && checkToken(c2, fromLoc)) return true;

  return false;
};

const CarSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get authentication state from Redux store
  const authState = useSelector((state) => state.auth);
  // Normalize user object - handle different API response formats (.data or .user)
  const user = authState.user?.data || authState.user?.user || authState.user;

  // Extract query parameters from URL FIRST - needed before state declarations
  const queryParams = new URLSearchParams(location.search);

  // Debug logging to track parameter extraction
  console.log("🔍 CarSelection - Current location:", location);
  console.log("🔍 CarSelection - Raw search params:", location.search);
  console.log("🔍 CarSelection - Parsed params:", Object.fromEntries(queryParams.entries()));

  const rawFromCity = queryParams.get("from") || queryParams.get("city") || "";
  const rawToCity = queryParams.get("to") || "";
  const rawFromLocation = queryParams.get("fromLocation") || rawFromCity;
  const rawToLocation = queryParams.get("toLocation") || rawToCity;

  const fromCity = getDisplayLocation(rawFromCity, rawFromLocation);
  const toCity = getDisplayLocation(rawToCity, rawToLocation);
  const fromLocation = getDisplayLocation(rawFromLocation, fromCity);
  const toLocation = getDisplayLocation(rawToLocation, toCity);
  const dateStr = queryParams.get("date");
  const timeStr = queryParams.get("time") || "10:00";
  const time = timeStr; // Alias for backward compatibility
  const journeyType = queryParams.get("journeyType") || "outstation";
  const tripType = queryParams.get("tripType") || "oneWay";
  const returnDateStr = queryParams.get("returnDate");
  const returnTimeStr = queryParams.get("returnTime");
  const serviceType = queryParams.get("service"); // rental, airport, or null for outstation
  const packageName = queryParams.get("package"); // for rental services
  const airportCode = queryParams.get("airport"); // for airport services
  const transferDirection = queryParams.get("direction"); // to/from for airport services
  const viaCitiesParam = queryParams.get("via"); // via cities for multi-city bookings
  const viaCities = parseViaCities(viaCitiesParam);
  // Extract distance from query parameter (for airport transfers with custom locations)
  const distanceParam = queryParams.get("distance");
  const initialDistance = distanceParam ? parseFloat(distanceParam) : null;

  // Check if pickup and destination are in the same city for outstation service
  const isOutstation = !serviceType || serviceType === "outstation" || journeyType === "outstation";
  const hasIntermediateStops = viaCities && viaCities.length > 0;
  const isSameCityOutstation = isOutstation && !hasIntermediateStops && isSameCityRoute(fromCity || fromLocation, toCity || toLocation);
  const isMultiDaySameCity = isSameCityOutstation && (tripType === "roundTrip" || journeyType === "roundTrip" || Boolean(returnDateStr));

  // Now declare state with initialDistance available
  const [isLoading, setIsLoading] = useState(!isSameCityOutstation);
  const [cabOptions, setCabOptions] = useState([]);
  const [selectedCab, setSelectedCab] = useState(null);
  const [distance, setDistance] = useState(initialDistance || null);
  const [cabPrices, setCabPrices] = useState({});
  const [baseRate, setBaseRate] = useState(12);
  const [error, setError] = useState(null);
  const [isFixedRoute, setIsFixedRoute] = useState(false);
  const [fixedRouteData, setFixedRouteData] = useState(null);

  // Airport and rental package data
  const [airports, setAirports] = useState([]);
  const [rentalPackages, setRentalPackages] = useState([]);
  const [airportsLoading, setAirportsLoading] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(true);

  // Trip time editing states
  const [isEditingTimes, setIsEditingTimes] = useState(false);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [timeValidationError, setTimeValidationError] = useState("");

  // Modal state for date/time selection
  const [showDateModal, setShowDateModal] = useState(false);
  const [isModifyingSearch, setIsModifyingSearch] = useState(false);
  const [isRouteExpanded, setIsRouteExpanded] = useState(false);
  const [showRoutePopup, setShowRoutePopup] = useState(false);

  // Google Maps autocomplete service
  const { autocompleteService } = useGoogleMaps() || {};

  // Goibibo-style Dropdown States
  const [showTripTypeDropdown, setShowTripTypeDropdown] = useState(false);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [showPickupDateDropdown, setShowPickupDateDropdown] = useState(false);
  const [showPickupTimeDropdown, setShowPickupTimeDropdown] = useState(false);
  const [showReturnDateDropdown, setShowReturnDateDropdown] = useState(false);
  const [showReturnTimeDropdown, setShowReturnTimeDropdown] = useState(false);
  const [showPickupCombinedDropdown, setShowPickupCombinedDropdown] = useState(false);
  const [showReturnCombinedDropdown, setShowReturnCombinedDropdown] = useState(false);

  // Outstation Trip Plan Modal States
  const [showTripPlanModal, setShowTripPlanModal] = useState(false);
  const [planTripType, setPlanTripType] = useState(tripType || "oneWay");
  const [planFrom, setPlanFrom] = useState(fromLocation || fromCity || "");
  const [planTo, setPlanTo] = useState(toLocation || toCity || "");
  const [planStops, setPlanStops] = useState(viaCities || []);

  // Staged Search State (Updates only when SEARCH button is clicked)
  const [selectedFrom, setSelectedFrom] = useState(fromLocation || fromCity || "");
  const [selectedTo, setSelectedTo] = useState(toLocation || toCity || "");
  const [selectedTripType, setSelectedTripType] = useState(tripType || "oneWay");
  const [selectedStops, setSelectedStops] = useState(viaCities || []);

  // Synchronize staged search state when URL parameters change
  useEffect(() => {
    setSelectedFrom(fromLocation || fromCity || "");
    setSelectedTo(toLocation || toCity || "");
    setSelectedTripType(tripType || "oneWay");
    setSelectedStops(viaCities || []);
  }, [fromLocation, fromCity, toLocation, toCity, tripType, viaCitiesParam]);

  // Modal Autocomplete Field State
  const [modalActiveField, setModalActiveField] = useState(null); // 'from', 'to', or `stop-${idx}`
  const [modalSuggestions, setModalSuggestions] = useState([]);

  // Search input queries and suggestions
  const [fromSearchQuery, setFromSearchQuery] = useState("");
  const [toSearchQuery, setToSearchQuery] = useState("");
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);

  // Bar container ref for outside click
  const barContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (barContainerRef.current && !barContainerRef.current.contains(event.target)) {
        setShowTripTypeDropdown(false);
        setShowFromDropdown(false);
        setShowToDropdown(false);
        setShowPickupDateDropdown(false);
        setShowPickupTimeDropdown(false);
        setShowReturnDateDropdown(false);
        setShowReturnTimeDropdown(false);
        setShowPickupCombinedDropdown(false);
        setShowReturnCombinedDropdown(false);
        setShowTripPlanModal(false);
        setModalActiveField(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reusable helper to fetch location suggestions (formatted with mainText and secondaryText like Goibibo)
  const fetchLocationPredictions = (inputQuery, callback) => {
    const popular = POPULAR_CITIES.map((c) => ({
      mainText: c.city,
      secondaryText: c.state,
      fullText: `${c.city}, ${c.state}`,
    }));

    if (!inputQuery || inputQuery.trim().length === 0) {
      callback(popular);
      return;
    }
    const query = inputQuery.trim();
    if (autocompleteService) {
      autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: "in" },
        },
        (predictions, status) => {
          if (status === "OK" && predictions && predictions.length > 0) {
            callback(
              predictions.map((p) => ({
                mainText:
                  p.structured_formatting?.main_text ||
                  p.description.split(",")[0].trim(),
                secondaryText:
                  p.structured_formatting?.secondary_text ||
                  p.description.split(",").slice(1).join(",").trim(),
                fullText: p.description,
              }))
            );
          } else {
            const filtered = POPULAR_CITIES
              .filter(
                (c) =>
                  c.city.toLowerCase().includes(query.toLowerCase()) ||
                  c.state.toLowerCase().includes(query.toLowerCase())
              )
              .map((c) => ({
                mainText: c.city,
                secondaryText: c.state,
                fullText: `${c.city}, ${c.state}`,
              }));
            callback(filtered.length > 0 ? filtered : popular);
          }
        }
      );
    } else {
      const filtered = POPULAR_CITIES
        .filter(
          (c) =>
            c.city.toLowerCase().includes(query.toLowerCase()) ||
            c.state.toLowerCase().includes(query.toLowerCase())
        )
        .map((c) => ({
          mainText: c.city,
          secondaryText: c.state,
          fullText: `${c.city}, ${c.state}`,
        }));
      callback(filtered.length > 0 ? filtered : popular);
    }
  };

  const handleTripTypeChange = (newType) => {
    setShowTripTypeDropdown(false);
    if (newType === "rental") {
      navigate(`/?service=rental`);
      return;
    }
    if (newType === "airport") {
      navigate(`/?service=airport`);
      return;
    }
    setSelectedTripType(newType);
    if (newType === "roundTrip") {
      if (!returnDate) {
        const d = new Date(pickupDate || new Date());
        d.setDate(d.getDate() + 1);
        setReturnDate(getLocalDateString(d));
      }
      if (!returnTime) {
        setReturnTime(pickupTime || "12:00 PM");
      }
    }
  };

  const handleFromSearchChange = (query) => {
    setFromSearchQuery(query);
    fetchLocationPredictions(query, setFromSuggestions);
  };

  const handleToSearchChange = (query) => {
    setToSearchQuery(query);
    fetchLocationPredictions(query, setToSuggestions);
  };

  const handleSelectFrom = (selectedPlace) => {
    setShowFromDropdown(false);
    setFromSearchQuery("");
    setSelectedFrom(selectedPlace);
  };

  const handleSelectTo = (selectedPlace) => {
    setShowToDropdown(false);
    setToSearchQuery("");
    setSelectedTo(selectedPlace);
  };

  const openTripPlanModal = () => {
    setShowTripTypeDropdown(false);
    setShowFromDropdown(false);
    setShowToDropdown(false);
    setShowPickupDateDropdown(false);
    setShowPickupTimeDropdown(false);
    setShowReturnDateDropdown(false);
    setShowReturnTimeDropdown(false);
    setShowPickupCombinedDropdown(false);
    setShowReturnCombinedDropdown(false);
    setPlanTripType(selectedTripType || tripType || "oneWay");
    setPlanFrom(selectedFrom || fromLocation || fromCity || "");
    setPlanTo(selectedTo || toLocation || toCity || "");
    setPlanStops(selectedStops && selectedStops.length > 0 ? [...selectedStops] : []);
    setModalActiveField(null);
    setModalSuggestions([]);
    setShowTripPlanModal(prev => !prev);
  };

  const handleApplyTripPlan = () => {
    setShowTripPlanModal(false);
    setModalActiveField(null);

    if (planFrom) {
      setSelectedFrom(planFrom);
    }
    if (planTo) {
      setSelectedTo(planTo);
    }

    const validStops = planStops.map((s) => s.trim()).filter(Boolean);
    setSelectedStops(validStops);
    setSelectedTripType(planTripType);

    if (planTripType === "roundTrip") {
      if (!returnDate) {
        const d = new Date(pickupDate || new Date());
        d.setDate(d.getDate() + 1);
        setReturnDate(getLocalDateString(d));
      }
      if (!returnTime) {
        setReturnTime(returnTime || pickupTime || "12:00 PM");
      }
    }
  };

  const handleApplySearch = () => {
    setShowTripTypeDropdown(false);
    setShowFromDropdown(false);
    setShowToDropdown(false);
    setShowPickupDateDropdown(false);
    setShowPickupTimeDropdown(false);
    setShowReturnDateDropdown(false);
    setShowReturnTimeDropdown(false);
    setShowPickupCombinedDropdown(false);
    setShowReturnCombinedDropdown(false);
    setShowTripPlanModal(false);
    setModalActiveField(null);

    const updatedParams = new URLSearchParams(location.search);
    const origin = selectedFrom || fromLocation || fromCity;
    const destination = selectedTo || toLocation || toCity;

    if (origin) {
      updatedParams.set("from", origin);
      updatedParams.set("fromLocation", origin);
    }
    if (destination) {
      updatedParams.set("to", destination);
      updatedParams.set("toLocation", destination);
    }
    if (selectedStops && selectedStops.length > 0) {
      updatedParams.set("via", selectedStops.join("|"));
    } else {
      updatedParams.delete("via");
    }

    const currentTripType = selectedTripType || tripType || "oneWay";
    updatedParams.set("tripType", currentTripType);
    if (currentTripType === "oneWay") {
      updatedParams.delete("returnDate");
      updatedParams.delete("returnTime");
    } else if (currentTripType === "roundTrip") {
      if (returnDate) updatedParams.set("returnDate", returnDate);
      if (returnTime) updatedParams.set("returnTime", returnTime);
    }

    if (pickupDate) updatedParams.set("date", pickupDate);
    if (pickupTime) updatedParams.set("time", pickupTime);

    if (updatedParams.get("service") === "rental" || updatedParams.get("service") === "airport") {
      updatedParams.delete("service");
    }

    navigate(`${location.pathname}?${updatedParams.toString()}`, { replace: true });
  };

  // Filter state
  const [selectedCabTypes, setSelectedCabTypes] = useState([]);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState([]);
  const [selectedModelTypes, setSelectedModelTypes] = useState([]);

  // Filter section expand/collapse state
  const [isCabTypeExpanded, setIsCabTypeExpanded] = useState(true);
  const [isFuelTypeExpanded, setIsFuelTypeExpanded] = useState(true);
  const [isModelTypeExpanded, setIsModelTypeExpanded] = useState(true);

  // Mobile drawers and sorting states
  const [showCarModelDrawer, setShowCarModelDrawer] = useState(false);
  const [showSortDrawer, setShowSortDrawer] = useState(false);
  const [showAllFiltersDrawer, setShowAllFiltersDrawer] = useState(false);
  const [sortBy, setSortBy] = useState("");

  const isCorporateBookingFlow =
    queryParams.get("corporate") === "1" ||
    sessionStorage.getItem("corporateBookingMode") === "1";

  // Debug extracted values
  console.log("🎯 CarSelection - Extracted values:", {
    fromCity, toCity, serviceType, packageName, dateStr, timeStr
  });

  // Fetch airports data
  const fetchAirports = async () => {
    try {
      setAirportsLoading(true);
      const response = await axios.get(`${API_URL}/airport`);
      if (response.data.success) {
        setAirports(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch airports:", error);
      toast.error("Failed to load airport transfers");
    } finally {
      setAirportsLoading(false);
    }
  };

  // Fetch rental packages data
  const fetchRentalPackages = async () => {
    try {
      setPackagesLoading(true);
      const response = await axios.get(`${API_URL}/rental-package`);
      if (response.data.success) {
        setRentalPackages(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch rental packages:", error);
      toast.error("Failed to load rental packages");
    } finally {
      setPackagesLoading(false);
    }
  };

  // Function to calculate distance between locations using Google Maps API (with caching and fast timeouts)
  const calculateDistance = async (fromLocation, toLocation) => {
    if (!fromLocation || !toLocation) return 100;
    const originClean = fromLocation.trim();
    const destClean = toLocation.trim();
    const cacheKey = `${originClean.toLowerCase()}->${destClean.toLowerCase()}`;
    if (distanceCache.has(cacheKey)) {
      return distanceCache.get(cacheKey);
    }

    try {
      // Check if Google Maps API is loaded
      if (
        window.google &&
        window.google.maps &&
        window.google.maps.DistanceMatrixService
      ) {
        const dist = await Promise.race([
          new Promise((resolve, reject) => {
            const service = new window.google.maps.DistanceMatrixService();

            service.getDistanceMatrix(
              {
                origins: [originClean],
                destinations: [destClean],
                travelMode: "DRIVING",
                unitSystem: window.google.maps.UnitSystem.METRIC,
              },
              (response, status) => {
                if (
                  status === "OK" &&
                  response.rows?.[0]?.elements?.[0]?.status === "OK"
                ) {
                  const distanceInKm =
                    response.rows[0].elements[0].distance.value / 1000; // Convert meters to km
                  resolve(distanceInKm);
                } else {
                  reject(new Error("Google Maps API failed"));
                }
              },
            );
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Google Maps timeout")), 1500)
          ),
        ]);

        if (dist && dist > 0) {
          distanceCache.set(cacheKey, dist);
          return dist;
        }
      }
    } catch (error) {
      console.warn("Google Maps JS API error/timeout:", error.message);
    }

    // Try backend API as fallback with fast 3500ms timeout
    try {
      const response = await axios.get(`${API_URL}/distance`, {
        params: {
          origin: originClean,
          destination: destClean,
        },
        timeout: 3500,
      });

      if (response.data.success && response.data.data) {
        const distanceInKm = response.data.data.distance;
        distanceCache.set(cacheKey, distanceInKm);
        return distanceInKm;
      }
    } catch (error) {
      console.warn("Backend API call failed:", error.message);
    }

    // Fallback to known distances for common routes
    const knownDistances = {
      "rajkot,ahmedabad": 214.47,
      "ahmedabad,rajkot": 214.47,
      "rajkot,delhi": 1140,
      "delhi,rajkot": 1140,
      "mumbai,pune": 149.5,
      "pune,mumbai": 149.5,
      "mumbai,delhi": 1420,
      "delhi,mumbai": 1420,
    };

    const routeKey = `${originClean.toLowerCase()},${destClean.toLowerCase()}`;
    if (knownDistances[routeKey]) {
      const d = knownDistances[routeKey];
      distanceCache.set(cacheKey, d);
      return d;
    }

    // If no known distance, use a default of 100km to prevent errors
    console.warn("No distance data available, using default 100km");
    return 100;
  };

  // Calculate distance for airport transfers if not provided in URL
  useEffect(() => {
    const calculateAirportDistance = async () => {
      // Only calculate for airport service if distance is not already provided
      if (
        serviceType === "airport" &&
        !initialDistance &&
        !distance &&
        calculateDistance &&
        ((transferDirection === "to" && fromCity) ||
          (transferDirection === "from" && toCity))
      ) {
        try {
          console.log(
            "📏 Calculating distance for airport transfer:",
            {
              transferDirection,
              from: transferDirection === "to" ? fromCity : toCity,
              calculateDistance: !!calculateDistance,
            }
          );

          // For airport transfers, calculate distance between the city and airport
          const origin = transferDirection === "to" ? fromCity : toCity;
          const destination = transferDirection === "to" ? toCity : fromCity;

          const calculatedDist = await calculateDistance(origin, destination);

          if (calculatedDist) {
            console.log(
              `📏 Distance calculated for airport transfer: ${calculatedDist} km`
            );
            setDistance(calculatedDist);
          }
        } catch (error) {
          console.error("Error calculating airport transfer distance:", error);
          // Use a default distance if calculation fails
          setDistance(50);
        }
      }
    };

    calculateAirportDistance();
  }, [serviceType, transferDirection, fromCity, toCity, initialDistance]);

  // Initialize trip time values when component mounts
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const today = new Date();
    const formattedToday = getLocalDateString(today);

    // Parse date from query parameter
    let dateFromQuery = params.get("date");
    if (dateFromQuery) {
      // Extract date part in local timezone and format to 'YYYY-MM-DD'
      const dateObj = new Date(dateFromQuery);
      if (!isNaN(dateObj.getTime())) {
        dateFromQuery = getLocalDateString(dateObj);
      } else {
        dateFromQuery = formattedToday; // Fallback if invalid
      }
    } else {
      dateFromQuery = formattedToday;
    }
    setPickupDate(dateFromQuery);

    // Parse time from query parameter
    let timeFromQuery = params.get("time");
    if (timeFromQuery) {
      timeFromQuery = timeFromQuery.trim().toLowerCase();
      // Use regex to parse hours, minutes and modifier, allowing optional space before am/pm
      const timeMatch = timeFromQuery.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        const modifier = timeMatch[3];

        if (modifier === "pm" && hours < 12) hours += 12;
        if (modifier === "am" && hours === 12) hours = 0;

        timeFromQuery = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      } else {
        // Fallback: extract numbers and modifiers manually
        const digitsOnly = timeFromQuery.replace(/[^0-9:]/g, "");
        const parts = digitsOnly.split(":");
        let hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        
        if (isNaN(hours) || isNaN(minutes)) {
          timeFromQuery = "10:00";
        } else {
          const isPm = timeFromQuery.includes("pm");
          const isAm = timeFromQuery.includes("am");
          if (isPm && hours < 12) hours += 12;
          if (isAm && hours === 12) hours = 0;
          timeFromQuery = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        }
      }
    } else {
      // Use current time as default instead of hardcoded 10:00
      const now = new Date();
      const currentHours = now.getHours().toString().padStart(2, "0");
      const currentMinutes = now.getMinutes().toString().padStart(2, "0");
      timeFromQuery = `${currentHours}:${currentMinutes}`;
    }
    setPickupTime(timeFromQuery);

    // Similar logic for return date and time if round trip
    if (tripType === "roundTrip") {
      let returnDateFromQuery = params.get("returnDate");
      if (returnDateFromQuery) {
        const returnDateObj = new Date(returnDateFromQuery);
        if (!isNaN(returnDateObj.getTime())) {
          returnDateFromQuery = getLocalDateString(returnDateObj);
        } else {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          returnDateFromQuery = getLocalDateString(tomorrow);
        }
      } else {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        returnDateFromQuery = getLocalDateString(tomorrow);
      }
      setReturnDate(returnDateFromQuery);

      let returnTimeFromQuery = params.get("returnTime");
      if (returnTimeFromQuery) {
        returnTimeFromQuery = returnTimeFromQuery.trim().toLowerCase();
        // Use regex to parse hours, minutes and modifier, allowing optional space before am/pm
        const returnTimeMatch = returnTimeFromQuery.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
        if (returnTimeMatch) {
          let hours = parseInt(returnTimeMatch[1], 10);
          const minutes = parseInt(returnTimeMatch[2], 10);
          const modifier = returnTimeMatch[3];

          if (modifier === "pm" && hours < 12) hours += 12;
          if (modifier === "am" && hours === 12) hours = 0;

          returnTimeFromQuery = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        } else {
          // Fallback: extract numbers and modifiers manually
          const digitsOnly = returnTimeFromQuery.replace(/[^0-9:]/g, "");
          const parts = digitsOnly.split(":");
          let hours = parseInt(parts[0], 10);
          const minutes = parseInt(parts[1], 10);
          
          if (isNaN(hours) || isNaN(minutes)) {
            returnTimeFromQuery = "10:00";
          } else {
            const isPm = returnTimeFromQuery.includes("pm");
            const isAm = returnTimeFromQuery.includes("am");
            if (isPm && hours < 12) hours += 12;
            if (isAm && hours === 12) hours = 0;
            returnTimeFromQuery = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
          }
        }
      } else {
        returnTimeFromQuery = "10:00"; // Default time if not provided
      }
      setReturnTime(returnTimeFromQuery);
    }
  }, [location.search, tripType]);

  // Validate trip times
  const validateTripTimes = () => {
    setTimeValidationError("");

    try {
      const pickupDateTime = parseDateTime(pickupDate, pickupTime);
      const now = new Date(
        new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
      );

      // Simplified pickup time validation as per user request: removed 30-minute buffer
      if (pickupDateTime < now) {
        const msg = "Pickup time cannot be in the past";
        setTimeValidationError(msg);
        return { valid: false, error: msg };
      }

      // For round trips, validate return date/time
      if (tripType === "roundTrip" && returnDate) {
        const returnDateTime = parseDateTime(returnDate, returnTime);

        // Ensure return is after pickup with minimum 1 hour difference
        const minReturnTime = new Date(pickupDateTime.getTime() + 60 * 60000); // pickup time + 1 hour

        if (returnDateTime < minReturnTime) {
          const msg = "Return time must be at least 1 hour after pickup time";
          setTimeValidationError(msg);
          return { valid: false, error: msg };
        }
      }

      return { valid: true, error: "" };
    } catch (error) {
      console.error("Date validation error:", error);
      const msg = "Invalid date format";
      setTimeValidationError(msg);
      return { valid: false, error: msg };
    }
  };

  // Update trip times and refresh cab prices
  const handleTimeUpdate = () => {
    const validation = validateTripTimes();
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Update URL parameters with new times
    const updatedParams = new URLSearchParams(location.search);
    updatedParams.set("date", pickupDate);
    updatedParams.set("time", pickupTime);

    if (tripType === "roundTrip") {
      updatedParams.set("returnDate", returnDate);
      updatedParams.set("returnTime", returnTime);
    }

    // Update URL using React Router to trigger state updates
    navigate(`${location.pathname}?${updatedParams.toString()}`, { replace: true });

    // Close the date/time modal and exit editing mode
    setShowDateModal(false);
    setIsEditingTimes(false);

    // Toast notification
    toast.success("Trip times updated successfully");
  };

  // State for popular route data
  const [isPopularRoute, setIsPopularRoute] = useState(false);
  const [popularRouteData, setPopularRouteData] = useState(null);


  // Global GST rate from settings
  const [gstRate, setGstRate] = useState(18);
  // Round trip minimum km per day (from admin booking settings)
  const [roundTripMinKmPerDay, setRoundTripMinKmPerDay] = useState(300);

  // Fetch global GST rate on mount
  useEffect(() => {
    const fetchGSTRate = async () => {
      try {
        const response = await axios.get(`${API_URL}/booking/settings/gst`);
        if (response.data.success) {
          // Force 18% GST - ignoring database value until it's updated
          setGstRate(18);
        }
      } catch (error) {
        console.error('Failed to fetch GST rate:', error);
        setGstRate(18); // Default to 18% if fetch fails
      }
    };

    fetchGSTRate();
  }, []);

  // Fetch public booking settings for round trip min km per day
  useEffect(() => {
    const fetchPublicSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/booking/settings/public`);
        if (response.data.success && response.data.data) {
          const minKm = response.data.data.roundTripMinKmPerDay;
          if (minKm && minKm > 0) {
            setRoundTripMinKmPerDay(minKm);
          }
        }
      } catch (error) {
        console.error('Failed to fetch public booking settings:', error);
      }
    };
    fetchPublicSettings();
  }, []);

  // Function to process popular route cabs and return them
  const getProcessedPopularRouteCabs = (popularRoute) => {
    if (!popularRoute || !popularRoute.cabTypePrices || popularRoute.cabTypePrices.length === 0) {
      return [];
    }

    return popularRoute.cabTypePrices.map((cabTypePrice) => {
      const cabType = cabTypePrice.cabTypeId;
      if (!cabType) {
        return null;
      }

      return {
        _id: cabType._id,
        name: cabType.name,
        description: cabType.description || "Popular route cab",
        category: cabType.category || "sedan",
        basePrice: cabTypePrice.price,
        price: cabTypePrice.price,
        totalPrice: cabTypePrice.price,
        popularRoutePrice: cabTypePrice.price,
        includedKm: 0,
        seatingCapacity: cabType.seatingCapacity || 4,
        luggageCapacity: cabType.luggageCapacity || 2,
        imageUrl:
          cabType.imageUrl ||
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3ECab Image%3C/text%3E%3C/svg%3E",
        acType: cabType.acType || "AC",
        isPopularRoute: true,
        popularRouteId: popularRoute._id,
        priceBreakdown: {
          basePrice: cabTypePrice.price,
          taxes: 0,
          tollCharges: 0,
          stateCharges: 0,
          total: cabTypePrice.price,
        },
      };
    }).filter(cab => cab !== null);
  };

  // Function to check if route exists in popular routes
  const checkPopularRoute = async (fromCity, toCity) => {
    try {
      if (!fromCity || !toCity) {
        return null;
      }

      // Extract city names from full addresses using smart extraction
      const { city: cleanFromCity } = extractCityAndState(fromCity);
      const { city: cleanToCity } = extractCityAndState(toCity);

      const effectiveFrom = cleanFromCity || (typeof fromCity === "string" && fromCity.trim().toLowerCase() !== "india" ? fromCity.trim() : null);
      const effectiveTo = cleanToCity || (typeof toCity === "string" && toCity.trim().toLowerCase() !== "india" ? toCity.trim() : null);

      if (!effectiveFrom || !effectiveTo) {
        console.log("Popular route check skipped: could not determine valid from and to cities", { fromCity, toCity });
        return null;
      }

      console.log("=== POPULAR ROUTE API CALL ===");
      console.log("Original fromCity:", fromCity);
      console.log("Original toCity:", toCity);
      console.log("Cleaned fromCity:", effectiveFrom);
      console.log("Cleaned toCity:", effectiveTo);

      const response = await axios.get(`${API_URL}/popular-routes/find`, {
        params: {
          from: effectiveFrom,
          to: effectiveTo,
        },
      });

      console.log("=== POPULAR ROUTE API RESULT ===");
      console.log("Status:", response.status);
      console.log("Data:", response.data);
      console.log("Has cabTypePrices:", !!response.data?.cabTypePrices);
      console.log("CabTypePrices length:", response.data?.cabTypePrices?.length);

      return response.data;
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 400) {
        console.log(`Popular route not found (${error.response?.status})`);
        return null;
      }
      console.warn("Popular route check failed, falling back to standard pricing:", error.message || error);
      return null;
    }
  };

  // Function to calculate distance between locations using Google Maps API
  // Function to calculate total distance with via cities in parallel
  const calculateDistanceWithViaCities = async (
    startCity,
    viaCities,
    endCity,
  ) => {
    console.log(
      `Calculating distance with via cities (parallel): ${startCity} → ${viaCities.join(" → ")} → ${endCity}`,
    );

    try {
      // Create array of all cities in order: [start, via1, via2, ..., end]
      const allCities = [startCity, ...viaCities, endCity].filter(Boolean);
      if (allCities.length < 2) {
        return { totalDistance: 100, segments: [] };
      }

      // Calculate distance for all segments concurrently with Promise.all
      const segmentPromises = [];
      for (let i = 0; i < allCities.length - 1; i++) {
        const fromCity = allCities[i];
        const toCity = allCities[i + 1];
        segmentPromises.push(
          calculateDistance(fromCity, toCity).then((segmentDistance) => ({
            from: fromCity,
            to: toCity,
            distance: segmentDistance,
          }))
        );
      }

      const segments = await Promise.all(segmentPromises);
      const totalDistance = segments.reduce((sum, seg) => sum + seg.distance, 0);

      console.log(`Total distance calculation:`, totalDistance, segments);

      return {
        totalDistance,
        segments,
      };
    } catch (error) {
      console.error("Error calculating distance with via cities:", error);
      const directDistance = await calculateDistance(startCity, endCity);
      const estimatedTotal = directDistance * 1.3;
      return {
        totalDistance: estimatedTotal,
        segments: [
          {
            from: startCity,
            to: endCity,
            distance: estimatedTotal,
          },
        ],
      };
    }
  };

  // Using the centralized price calculation function from utils
  const calculatePrice = (cab, dist) => {
    // Use rounded distance for consistent calculation
    const roundedDist = Math.round(dist);
    return calculateCabPrice(cab, roundedDist);
  };

  // Helper function to get cab price based on base rate per km only
  // Applies round trip minimum KM per day rule if configured in admin booking settings
  const calculateDynamicPrice = (cab) => {
    // Get base price per km from cab or use default
    const baseKmPrice = Number(cab.baseKmPrice) || 11.88;
    const extraKmRate = Number(cab.extraFarePerKm) || baseKmPrice * 1.2;

    // Use rounded distance for calculation to match display
    const roundedDistance = Math.round(distance || 0);

    if (tripType === "roundTrip") {
      // Calculate calendar days between pickupDate and returnDate
      let numDays = 1;
      const pDate = pickupDate || dateStr;
      const rDate = returnDate || returnDateStr;
      if (pDate && rDate) {
        try {
          const start = new Date(pDate);
          const end = new Date(rDate);
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
            const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
            const diffDays = Math.round((endUTC - startUTC) / (1000 * 60 * 60 * 24));
            numDays = Math.max(1, diffDays + 1);
          }
        } catch (e) {
          numDays = 1;
        }
      }

      // Minimum KM per day rule for round trips
      const minPerDay = Number(roundTripMinKmPerDay) || 300;
      const minTotalKm = minPerDay * numDays;
      const actualRoundTripKm = roundedDistance * 2;
      const effectiveDistance = Math.max(actualRoundTripKm, minTotalKm);

      const totalPrice = Math.round(baseKmPrice * effectiveDistance);

      return {
        basePrice: totalPrice,
        extraKmPrice: 0,
        totalPrice: totalPrice,
        includedKm: effectiveDistance,
        extraKm: 0,
        baseKmPrice,
        extraKmRate: Math.round(extraKmRate),
        outerStationSurcharge: 0,
        roundTripMinApplied: effectiveDistance > actualRoundTripKm,
        minKmPerDay: minPerDay,
        numDays: numDays,
        minTotalKm: minTotalKm,
        actualRoundTripKm: actualRoundTripKm,
      };
    }

    // One-way calculation
    const totalPrice = Math.round(baseKmPrice * roundedDistance);

    return {
      basePrice: totalPrice,
      extraKmPrice: 0, // Not used in calculation
      totalPrice: totalPrice,
      includedKm: roundedDistance, // Show effective distance as included km
      extraKm: 0, // Not used
      baseKmPrice,
      extraKmRate: Math.round(extraKmRate), // Show extra km rate in UI
      outerStationSurcharge: 0, // No surcharge applied
    };
  };

  // Function to process fixed route cabs
  const processFixedRouteCabs = (fixedRoutes) => {
    const fixedCabs = fixedRoutes.map((route) => {
      const cabDetails = route.cabType;

      // Skip if cabDetails is null or undefined
      if (!cabDetails) {
        console.warn("Route found with null cabType:", route);
        return null;
      }

      return {
        _id: cabDetails._id,
        name: cabDetails.name,
        description: cabDetails.description || "Fixed route cab",
        category: cabDetails.category || "sedan",
        basePrice: route.price,
        price: route.price, // Same price for fixed routes
        totalPrice: route.price, // All inclusive price
        includedKm: 0, // No included kilometers as per simplified approach
        seatingCapacity: cabDetails.seatingCapacity || 4,
        luggageCapacity: cabDetails.luggageCapacity || 2,
        imageUrl:
          cabDetails.imageUrl ||
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3ECab Image%3C/text%3E%3C/svg%3E",
        acType: cabDetails.acType || "AC",
        isFixedRoute: true,
        fixedRouteId: route._id,
        estimatedTime: route.estimatedTime,
        distance: route.distance,
        // Calculate price breakdown for display purposes
        priceBreakdown: {
          basePrice: route.price,
          taxes: 0, // Already included in fixed price
          tollCharges: 0, // Already included
          stateCharges: 0, // Already included
          total: route.price,
        },
      };
    }).filter(cab => cab !== null); // Filter out null values

    setCabOptions(fixedCabs);
    if (fixedCabs.length > 0) {
      setSelectedCab(fixedCabs[0]);
    }
  };

  // Function to process popular route cabs
  const processPopularRouteCabs = (popularRoute) => {
    const popularCabs = getProcessedPopularRouteCabs(popularRoute);

    if (popularCabs.length === 0) {
      console.warn("No cabs were processed from popular route");
      return;
    }

    setCabOptions(popularCabs);
    setSelectedCab(popularCabs[0]);
  };

  // Note: We're using a simplified pricing model: price = distance * rate
  // No additional fees or calculations are needed

  // Helper function to get cab price - uses admin panel prices for airports/rentals with dynamic fallback
  // AIRPORT PRICING LOGIC:
  // 1. If admin panel has configured pricing for the airport → Use fixed admin price
  // 2. If NO admin pricing found (data not matched) → Use distance-based calculation:
  //    - Calculate distance between pickup and airport (or airport and drop location)
  //    - Multiply distance by cab's baseKmPrice per km
  //    - Apply outer station surcharges for long distances (100km+)
  //    This ensures all airport transfers have a fair price based on distance, even if admin hasn't configured them
  const getCabPrice = (cab) => {
    // Null check for cab object
    if (!cab || !cab.name) {
      console.warn("getCabPrice called with invalid cab:", cab);
      return {
        originalPrice: 0,
        totalPrice: 0,
        basePrice: 0,
        extraKmPrice: 0,
        includedKm: 0,
        extraKm: 0,
        baseKmPrice: 0,
        extraKmRate: 0,
      };
    }

    // Check if this is an airport transfer or rental package journey
    // (service, packageName, and airportCode are already extracted at component level)

    // For airport transfers - use admin panel pricing with dynamic fallback
    if (serviceType === "airport" && airportCode) {
      const airport = airports.find((a) => a.code === airportCode);
      if (airport) {
        // Extract city name from fromCity using robust location matcher
        // Handles full addresses like "Ground Floor, F3G7+4G3, Gurudwara Rd, Jamnagar, Gujarat 361006"
        const { city: extractedCity } = extractCityAndState(fromCity || fromLocation);
        const pickupCityName = extractedCity
          ? extractedCity.toLowerCase().trim()
          : fromCity.split(',')[0].trim().toLowerCase(); // Fallback to naive split if extraction fails

        console.log(`📍 Processing airport pricing for ${airportCode}:`, {
          cabName: cab.name,
          pickupCity: pickupCityName,
          extractedFromAddress: extractedCity,
          serviceCitiesCount: airport.serviceCities?.length || 0,
          hasServiceCities: !!airport.serviceCities
        });

        // Check service cities first - BUT validate that the service city matches the pickup location
        if (airport.serviceCities && airport.serviceCities.length > 0) {
          for (const serviceCity of airport.serviceCities) {
            const { city: serviceCityExtracted } = extractCityAndState(serviceCity.cityName || '');
            const serviceCityName = serviceCityExtracted
              ? serviceCityExtracted.toLowerCase().trim()
              : (serviceCity.cityName || '').split(',')[0].trim().toLowerCase(); // Fallback

            console.log(`  📍 Checking service city: ${serviceCity.cityName || 'unknown'}`, {
              cabPricesKeys: Object.keys(serviceCity.cabPrices || {}),
              lookingFor: cab.name,
              serviceCityMatch: serviceCityName === pickupCityName ? '✅ MATCH' : '❌ NO MATCH'
            });

            // Only use pricing if the service city matches the pickup location
            if (serviceCityName === pickupCityName) {
              const cabPrice = cab.name ? serviceCity.cabPrices?.[cab.name] : null;
              if (cabPrice) {
                console.log(`✅ Airport pricing found for ${cab.name} in ${serviceCity.cityName}: ₹${cabPrice}`);
                return {
                  originalPrice: cabPrice,
                  totalPrice: cabPrice,
                  basePrice: cabPrice,
                  extraKmPrice: 0,
                  includedKm: 0,
                  extraKm: 0,
                  baseKmPrice: 0,
                  extraKmRate: 0,
                };
              }
            } else {
              console.log(`  ⚠️ Service city ${serviceCity.cityName} does NOT match pickup city ${pickupCityName}. Skipping this pricing.`);
            }
          }
        }
        // Fallback to legacy cab type pricing
        if (airport.cabTypePrices && airport.cabTypePrices.length > 0 && cab.name) {
          const cabTypePricing = airport.cabTypePrices.find(
            (cp) => cp.cabTypeId?.name === cab.name,
          );
          if (cabTypePricing) {
            console.log(`✅ Airport cab type pricing found for ${cab.name}: ₹${cabTypePricing.price}`);
            return {
              originalPrice: cabTypePricing.price,
              totalPrice: cabTypePricing.price,
              basePrice: cabTypePricing.price,
              extraKmPrice: 0,
              includedKm: 0,
              extraKm: 0,
              baseKmPrice: 0,
              extraKmRate: 0,
            };
          }
        }

        // Fallback: If no matching admin data found, use distance-based dynamic pricing (like outstation)
        console.warn(`⚠️ No airport pricing data found for ${cab.name} at ${airportCode}. Using distance-based calculation.`);
        console.log(`📍 Airport: ${airport.name} (${airportCode})`);
        console.log(`📍 Distance available: ${distance}km`);
        console.log(`📍 Cab baseKmPrice: ₹${cab.baseKmPrice || 'unknown'}/km`);
        if (!distance || distance === null) {
          console.warn(`⚠️ WARNING: Distance is ${distance}. Using fallback distance calculation.`);
        }

        // Use dynamic distance-based pricing as fallback
        const priceInfo = calculateDynamicPrice(cab);
        const discountPercent = cab.discount || 0;
        const originalPrice = priceInfo.totalPrice || 0;
        const discountedPrice = Math.round(originalPrice - (originalPrice * discountPercent / 100));
        return {
          originalPrice: originalPrice,
          totalPrice: discountedPrice,
          basePrice: priceInfo.basePrice || 0,
          extraKmPrice: priceInfo.extraKmPrice || 0,
          includedKm: priceInfo.includedKm || 0,
          extraKm: priceInfo.extraKm || 0,
          baseKmPrice: priceInfo.baseKmPrice || 0,
          extraKmRate: priceInfo.extraKmRate || 0,
          discount: discountPercent,
          isAirportFallback: true, // Mark that this is using fallback pricing
        };
      }
    }

    // For rental packages - use admin panel pricing
    if (serviceType === "rental" && packageName) {
      const pkg = rentalPackages.find((p) => p.name === packageName);
      if (pkg) {
        // Check service cities first
        if (pkg.serviceCities && pkg.serviceCities.length > 0) {
          for (const serviceCity of pkg.serviceCities) {
            const cabPrice = cab.name ? serviceCity.cabPrices?.[cab.name] : null;
            if (cabPrice) {
              const discountPercent = cab.discount || 0;
              const discountedPrice = Math.round(cabPrice - (cabPrice * discountPercent / 100));
              return {
                originalPrice: cabPrice,
                totalPrice: discountedPrice,
                basePrice: cabPrice,
                extraKmPrice: 0,
                includedKm: 0,
                extraKm: 0,
                baseKmPrice: 0,
                extraKmRate: 0,
                discount: discountPercent,
              };
            }
          }
        }
        // Fallback to legacy cab type pricing
        if (pkg.cabTypePrices && pkg.cabTypePrices.length > 0 && cab.name) {
          const cabTypePricing = pkg.cabTypePrices.find(
            (cp) => cp.cabTypeId?.name === cab.name,
          );
          if (cabTypePricing) {
            const discountPercent = cab.discount || 0;
            const discountedPrice = Math.round(cabTypePricing.price - (cabTypePricing.price * discountPercent / 100));
            return {
              originalPrice: cabTypePricing.price,
              totalPrice: discountedPrice,
              basePrice: cabTypePricing.price,
              extraKmPrice: 0,
              includedKm: 0,
              extraKm: 0,
              baseKmPrice: 0,
              extraKmRate: 0,
              discount: discountPercent,
            };
          }
        }
      }
    }

    // For rental packages with fixed pricing (similar to popular routes)
    if (cab.isRentalPackage && cab.rentalPackagePrice) {
      const discountPercent = cab.discount || 0;
      const discountedPrice = Math.round(cab.rentalPackagePrice - (cab.rentalPackagePrice * discountPercent / 100));
      return {
        originalPrice: cab.rentalPackagePrice,
        totalPrice: discountedPrice,
        basePrice: cab.rentalPackagePrice,
        extraKmPrice: 0,
        includedKm: cab.includedKm || 0,
        extraKm: 0,
        baseKmPrice: cab.baseKmPrice || 0,
        extraKmRate: cab.extraFarePerKm || cab.extraKmRate || 0,
        discount: discountPercent,
      };
    }
    // For popular routes with fixed pricing
    else if (cab.isPopularRoute && cab.popularRoutePrice) {
      // Apply round trip multiplier (2x) for round trips since popular route prices are one-way
      const roundTripMultiplier = tripType === "roundTrip" ? 2 : 1;
      const baseRoutePrice = Math.round(cab.popularRoutePrice * roundTripMultiplier);
      const discountPercent = cab.discount || 0;
      const discountedPrice = Math.round(baseRoutePrice - (baseRoutePrice * discountPercent / 100));
      return {
        originalPrice: baseRoutePrice,
        totalPrice: discountedPrice,
        basePrice: baseRoutePrice,
        extraKmPrice: 0,
        includedKm: cab.includedKm || 0,
        extraKm: 0,
        baseKmPrice: cab.baseKmPrice || 0,
        extraKmRate: cab.extraFarePerKm || cab.extraKmRate || 0,
        discount: discountPercent,
      };
    }
    // For fixed routes, return a simplified price object
    else if (cab.isFixedRoute) {
      // Apply round trip multiplier (2x) for round trips since fixed route prices are one-way
      const roundTripMultiplier = tripType === "roundTrip" ? 2 : 1;
      const baseRoutePrice = Math.round(cab.price * roundTripMultiplier);
      const discountPercent = cab.discount || 0;
      const discountedPrice = Math.round(baseRoutePrice - (baseRoutePrice * discountPercent / 100));
      return {
        originalPrice: baseRoutePrice,
        totalPrice: discountedPrice,
        basePrice: baseRoutePrice,
        extraKmPrice: 0,
        includedKm: cab.includedKm || 0,
        extraKm: 0,
        baseKmPrice: 0,
        extraKmRate: cab.extraFarePerKm || cab.extraKmRate || 0,
        discount: discountPercent,
      };
    } else {
      // Use the dynamic pricing function which returns the full price object
      const priceInfo = calculateDynamicPrice(cab);
      const discountPercent = cab.discount || 0;
      const originalPrice = priceInfo.totalPrice || 0;
      const discountedPrice = Math.round(originalPrice - (originalPrice * discountPercent / 100));
      // Ensure we have all required fields
      return {
        originalPrice: originalPrice,
        totalPrice: discountedPrice,
        basePrice: priceInfo.basePrice || 0,
        extraKmPrice: priceInfo.extraKmPrice || 0,
        includedKm: priceInfo.includedKm || 0,
        extraKm: priceInfo.extraKm || 0,
        baseKmPrice: priceInfo.baseKmPrice || 0,
        extraKmRate: priceInfo.extraKmRate || 0,
        discount: discountPercent,
        roundTripMinApplied: priceInfo.roundTripMinApplied,
        minKmPerDay: priceInfo.minKmPerDay,
        numDays: priceInfo.numDays,
        minTotalKm: priceInfo.minTotalKm,
        actualRoundTripKm: priceInfo.actualRoundTripKm,
      };
    }
  };

  // Shared handler for booking booking clicks
  const handleCabBooking = (e, cab) => {
    e.stopPropagation();

    // Set as selected cab
    setSelectedCab(cab);

    // Always show the Trip Details modal regardless of authentication status
    // Authentication will be checked when user clicks "Continue to Booking"
    setIsModifyingSearch(false);
    setShowDateModal(true);
  };

  // Function to fetch real cab data from the database
  const fetchCabData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setIsFixedRoute(false);
      setFixedRouteData(null);
      setIsPopularRoute(false);
      setPopularRouteData(null);

      // FIRST: Check if this is a popular route with fixed pricing (skip for rental services AND when via cities exist)
      let popularRoute = null;
      if (serviceType !== "rental" && viaCities.length === 0) {
        try {
          console.log("=== CHECKING POPULAR ROUTE ===");
          console.log("Service type:", serviceType);
          console.log("From city:", fromCity);
          console.log("To city:", toCity);

          popularRoute = await checkPopularRoute(fromCity, toCity);

          if (popularRoute) {
            console.log("=== POPULAR ROUTE FOUND ===");
            console.log("Route data:", popularRoute);
            console.log("✅ Using FIXED DATABASE PRICING from route:", popularRoute.from, "→", popularRoute.to);

            setIsPopularRoute(true);
            setPopularRouteData(popularRoute);

            // Set fixed distance from popular route
            const fixedDistance =
              parseFloat(popularRoute.distance.replace(/[^\d.]/g, "")) || 100;
            setDistance(fixedDistance);

            // Process the popular route cab type prices into cab options
            console.log("Processing popular route cabs...");
            processPopularRouteCabs(popularRoute);
            setIsLoading(false);
            console.log("=== POPULAR ROUTE PROCESSED, RETURNING ===");

            // Return processed cabs so loadRealCabData doesn't error
            return getProcessedPopularRouteCabs(popularRoute);
          } else {
            console.log("=== NO POPULAR ROUTE FOUND ===");
            console.log("⚠️ Route NOT in database. Will use DYNAMIC DISTANCE-BASED pricing instead.");
          }
        } catch (popularRouteError) {
          console.log(
            "⚠️ Popular route check failed. Will use dynamic pricing.",
            popularRouteError.message
          );
          // Continue with normal flow if popular route check fails
        }
      } else {
        console.log("Rental service detected - skipping popular routes check");
      }

      // Check if this is a fixed route (skip for rental, airport services, AND when via cities exist)
      if (serviceType !== "rental" && serviceType !== "airport" && viaCities.length === 0) {
        try {
          // First, get city IDs from names
          const cityResponse = await axios.get(`${API_URL}/city`);
          if (cityResponse.data.success) {
            const cities = cityResponse.data.data;
            // Extract city names from full addresses using smart extraction
            const { city: cleanFromCity } = extractCityAndState(fromCity);
            const { city: cleanToCity } = extractCityAndState(toCity);
            const fromCityObj = cities.find(
              (city) => city.name.toLowerCase() === cleanFromCity?.toLowerCase(),
            );
            const toCityObj = cities.find(
              (city) => city.name.toLowerCase() === cleanToCity?.toLowerCase(),
            );

            if (fromCityObj && toCityObj) {
              // Check for fixed route
              const fixedRoutes = await checkFixedRoute(
                fromCityObj._id,
                toCityObj._id,
              );

              if (fixedRoutes && fixedRoutes.length > 0) {
                // We have fixed routes!
                setIsFixedRoute(true);
                setFixedRouteData(fixedRoutes);

                // Process the fixed routes into cab options
                processFixedRouteCabs(fixedRoutes);
                setIsLoading(false);
                return;
              }
            }
          }
        } catch (fixedRouteError) {
          console.error("Error checking for fixed route:", fixedRouteError);
          // Continue with normal flow if fixed route check fails
        }
      } else {
        console.log(
          "Rental/Airport service detected - skipping fixed routes check",
        );
      }

      // For non-popular routes, calculate distance and fetch cabs normally

      // Always calculate the distance to ensure we have the latest value
      try {
        // For Rajkot to Delhi, we know this is a special route with fixed distance of 1140 km
        let dist;
        if (
          (fromCity.toLowerCase() === "rajkot" &&
            toCity.toLowerCase() === "delhi") ||
          (fromCity.toLowerCase() === "delhi" &&
            toCity.toLowerCase() === "rajkot")
        ) {
          dist = 1140; // Actual road distance from Rajkot to Delhi in km
          console.log(`Using known distance for Rajkot-Delhi route: ${dist}km`);
        } else {
          // For other routes, calculate distance from API
          if (viaCities.length > 0) {
            console.log(
              `Route has ${viaCities.length} via cities: ${viaCities.join(", ")}`,
            );
            const distanceResult = await calculateDistanceWithViaCities(
              fromCity,
              viaCities,
              toCity,
            );
            dist = distanceResult.totalDistance;
            // Store segments for later use if needed
            window.currentRouteSegments = distanceResult.segments;
            console.log(
              `Successfully calculated total distance via cities: ${dist}km`,
            );
          } else {
            dist = await calculateDistance(fromCity, toCity);
            console.log(
              `Successfully calculated direct distance from ${fromCity} to ${toCity}: ${dist}km`,
            );
          }
        }

        // Always update the distance state
        setDistance(dist);
      } catch (distError) {
        console.error("Error calculating distance:", distError);
        setError("Could not calculate distance between locations.");
        setIsLoading(false);
        return;
      }

      console.log(`Searching for cabs in ${fromCity}`);

      let response;

      // For rental services, fetch cab types directly instead of checking availability
      if (serviceType === "rental") {
        console.log("Rental service - fetching cab types directly");
        response = await axios.get(`${API_URL}/cabtype/all`);

        // Process the cabs to add rental package pricing similar to popular routes
        if (response.data.success && response.data.data) {
          const cabs = response.data.data;
          const selectedPackage = rentalPackages.find(
            (pkg) => pkg.name === packageName,
          );

          if (selectedPackage) {
            console.log(
              "Processing rental package pricing for cabs:",
              selectedPackage,
            );

            // Add rental package pricing to each cab, similar to popular routes
            const cabsWithRentalPricing = cabs.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id).map((cab) => {
              // Look for cab price in the selected package
              let cabPrice = null;

              // Check service cities first
              if (
                selectedPackage.serviceCities &&
                selectedPackage.serviceCities.length > 0
              ) {
                for (const serviceCity of selectedPackage.serviceCities) {
                  if (
                    serviceCity.cabPrices &&
                    cab.name &&
                    serviceCity.cabPrices[cab.name]
                  ) {
                    cabPrice = serviceCity.cabPrices[cab.name];
                    break;
                  }
                }
              }

              // Fallback to legacy cab type pricing
              if (
                !cabPrice &&
                selectedPackage.cabTypePrices &&
                selectedPackage.cabTypePrices.length > 0
              ) {
                const cabTypePricing = selectedPackage.cabTypePrices.find(
                  (cp) =>
                    (cab.name && cp.cabTypeId?.name === cab.name) ||
                    cp.cabTypeId?._id === cab._id,
                );
                if (cabTypePricing) {
                  cabPrice = cabTypePricing.price;
                }
              }

              // If we found a price, mark this cab as having rental package pricing
              if (cabPrice) {
                return {
                  ...cab,
                  isRentalPackage: true,
                  rentalPackagePrice: cabPrice,
                  rentalPackageName: selectedPackage.name,
                  rentalPackageId: selectedPackage._id,
                };
              }

              return cab;
            });

            // Override the response data with processed cabs
            response.data.data = cabsWithRentalPricing;
          }
        }
      } else {
        // For outstation/airport services, check availability
        console.log("🚗 Checking cab availability for outstation/airport service");
        console.log("📍 API URL:", API_URL);
        console.log("📍 Request params:", {
          journeyType,
          pickupLocation: fromCity,
          dropLocation: toCity,
          pickupDate: dateStr,
          returnDate: tripType === "roundTrip" ? dateStr : null,
        });

        try {
          response = await axios.post(`${API_URL}/cab/available`, {
            journeyType: journeyType,
            pickupLocation: fromCity,
            dropLocation: toCity,
            pickupDate: dateStr,
            returnDate: tripType === "roundTrip" ? dateStr : null,
          });

          console.log("✅ Availability check response:", response.data);

          // If availability check returns no cabs, fallback to fetching all cabs
          if (!response.data.success || !response.data.data || response.data.data.length === 0) {
            console.log("⚠️ No cabs from availability check, fetching all cabs as fallback");
            console.log("📍 Fallback API URL:", `${API_URL}/cabtype/all`);

            try {
              response = await axios.get(`${API_URL}/cabtype/all`);
              console.log("✅ Fallback response:", response.data);
            } catch (fallbackError) {
              console.error("❌ Fallback also failed:", fallbackError);
              console.error("❌ Error details:", {
                message: fallbackError.message,
                response: fallbackError.response?.data,
                status: fallbackError.response?.status,
              });
              throw fallbackError;
            }
          }
        } catch (availabilityError) {
          console.error("❌ Availability check failed:", availabilityError);
          console.error("❌ Error details:", {
            message: availabilityError.message,
            response: availabilityError.response?.data,
            status: availabilityError.response?.status,
            url: `${API_URL}/cab/available`,
          });

          // Fallback to fetching all cab types if availability check fails
          console.log("🔄 Attempting fallback to fetch all cabs...");
          try {
            response = await axios.get(`${API_URL}/cabtype/all`);
            console.log("✅ Fallback successful:", response.data);
          } catch (fallbackError) {
            console.error("❌ Fallback also failed:", fallbackError);
            console.error("❌ Complete failure - no cabs can be fetched");
            throw new Error(
              `Failed to fetch cabs from both endpoints. ` +
              `Primary error: ${availabilityError.message}. ` +
              `Fallback error: ${fallbackError.message}`
            );
          }
        }
      }

      console.log("📊 Response data structure:", {
        hasData: !!response.data,
        success: response.data?.success,
        dataLength: response.data?.data?.length,
        dataType: Array.isArray(response.data?.data) ? 'array' : typeof response.data?.data,
      });

      if (!response.data.success) {
        console.error("❌ Response not successful:", response.data);
        throw new Error(response.data.message || "Failed to fetch cabs");
      }

      let cabs = response.data.data;
      console.log(`Found ${cabs.length} cabs from backend in ${fromCity}`);

      // CRITICAL FIX: Adjust any unrealistically low baseKmPrice values
      // Some cabs in database may have baseKmPrice as absolute values (10, 14) instead of per-km rates
      cabs = cabs.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id).map((cab) => {
        // If baseKmPrice is unrealistically low for a per-km rate (< 5 rupees per km)
        // interpret as a base price and convert to a more realistic per-km rate
        if (
          (cab.baseKmPrice < 5 ||
            cab.baseKmPrice === 10 ||
            cab.baseKmPrice === 14) &&
          distance > 50
        ) {
          const adjustedCab = { ...cab };
          // Store original price for reference
          adjustedCab.originalBasePrice = cab.baseKmPrice;

          // Set to a more realistic per-km rate (based on cab category)
          if (cab.category?.toLowerCase().includes("luxury")) {
            adjustedCab.baseKmPrice = 18; // Luxury cab rate
          } else if (cab.category?.toLowerCase().includes("suv")) {
            adjustedCab.baseKmPrice = 15; // SUV rate
          } else {
            adjustedCab.baseKmPrice = 12; // Standard sedan rate
          }

          // Also ensure there's some extraFarePerKm if it's missing
          if (!adjustedCab.extraFarePerKm || adjustedCab.extraFarePerKm < 1) {
            adjustedCab.extraFarePerKm = adjustedCab.baseKmPrice * 1.2;
          }

          console.log(
            `Adjusted cab ${cab.name} baseKmPrice from ${cab.baseKmPrice} to ${adjustedCab.baseKmPrice} rupees/km`,
          );
          return adjustedCab;
        }
        return cab;
      });

      // Handle no cabs found situation
      if (!cabs || cabs.length === 0) {
        console.warn("No cabs found after all attempts");
        setError(
          `No cabs available for this route. This could be because:\n` +
          `• No cabs are configured in the system\n` +
          `• The route is not covered by our service\n\n` +
          `Please try a different route or contact support.`,
        );
        setIsLoading(false);
        return cabs || [];
      }

      // If popular route found, modify cabs with popular route pricing and distance
      if (popularRoute) {
        console.log("Applying popular route pricing to regular cabs");
        const fixedDistance =
          parseFloat(popularRoute.distance.replace(/[^\d.]/g, "")) || 100;

        cabs = cabs.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id).map((cab) => {
          // Find the specific price for this cab type from the popular route
          let cabPrice = null;

          if (
            popularRoute.cabTypePrices &&
            popularRoute.cabTypePrices.length > 0
          ) {
            const cabTypePrice = popularRoute.cabTypePrices.find(
              (ctp) =>
                ctp.cabTypeId._id === cab._id || ctp.cabTypeId === cab._id,
            );
            if (cabTypePrice) {
              cabPrice = cabTypePrice.price;
            }
          }

          // If no specific price found for this cab type, use dynamic pricing
          if (!cabPrice) {
            return cab; // Keep original cab without modification
          }

          // Return modified cab with popular route pricing but keep all other properties
          return {
            ...cab,
            isPopularRoute: true,
            popularRouteId: popularRoute._id,
            // Override pricing fields
            baseKmPrice: cabPrice / fixedDistance, // Calculate per km rate
            totalPrice: cabPrice,
            popularRoutePrice: cabPrice,
            popularRouteDistance: popularRoute.distance,
          };
        });
      }

      return cabs;
    } catch (error) {
      console.error("Error fetching cab data:", error);
      throw error;
    }
  };

  useEffect(() => {
    // Validation logic based on service type
    if (serviceType === "rental") {
      // For rental services, only fromCity (selected city) is required
      if (!fromCity) {
        toast.error("Missing location information for rental service");
        navigate("/");
        return;
      }
    } else if (serviceType === "airport") {
      // For airport services, validate based on direction
      if (transferDirection === "to") {
        if (!fromCity) {
          toast.error("Missing pickup city for airport transfer");
          navigate("/");
          return;
        }
      } else if (transferDirection === "from") {
        if (!toCity) {
          toast.error("Missing drop city for airport transfer");
          navigate("/");
          return;
        }
      }
    } else {
      // For outstation services, both cities are required
      if (!fromCity || !toCity) {
        toast.error("Missing location information");
        navigate("/");
        return;
      }

      // Check if pickup and destination cities are the same for outstation
      if (isSameCityOutstation) {
        console.warn("⚠️ Pickup and destination cities are the same for outstation service. Showing same-city empty state.");
        setIsLoading(false);
        setCabOptions([]);
        setError(null);
        return;
      }
    }

    const loadRealCabData = async () => {
      try {
        setIsLoading(true);
        // Reset states to prevent showing stale data
        // Only reset if we don't already have popular/fixed route cabs loaded and no cabs yet
        if (!isPopularRoute && !isFixedRoute && cabOptions.length === 0) {
          setCabOptions([]);
          setError("");
          setSelectedCab(null);
        } else {
          console.log("Preserving existing cab options while loading new search results");
        }

        // 1. Fetch real cab data from the API
        const cabs = await fetchCabData();

        // 2. Calculate pricing for each cab based on distance using our utility function
        const prices = {};
        if (Array.isArray(cabs)) {
          cabs.forEach((cab) => {
            prices[cab._id] = getCabPrice(cab);
          });
        }

        setCabPrices(prices);

        // Log pricing source for transparency
        if (!isPopularRoute && !isFixedRoute) {
          console.log("💰 PRICING SOURCE: Dynamic distance-based pricing (route not in database)");
          console.log("Distance used:", distance, "km");
        } else if (isPopularRoute) {
          console.log("💰 PRICING SOURCE: Fixed database pricing from popular route");
        } else if (isFixedRoute) {
          console.log("💰 PRICING SOURCE: Fixed database pricing from fixed route");
        }

        // Filter cabs based on service type to prevent showing wrong service data
        let filteredCabs = cabs || [];

        console.log("🚗 Raw cabs data from API:", cabs);
        console.log("🔍 Service type:", serviceType);
        console.log("🔍 Journey type:", journeyType);

        // For outstation services, only show regular cabs (not airport or rental specific)
        if (!serviceType || serviceType === "outstation") {
          console.log(
            "📌 Filtering for outstation service - excluding airport/rental cabs",
          );
          filteredCabs = filteredCabs.filter(
            (cab) =>
              !cab.isAirportTransfer &&
              !cab.isRentalPackage &&
              (!cab.serviceType || cab.serviceType === "outstation"),
          );
          console.log("✅ Filtered cabs for outstation:", filteredCabs);
        }
        // For airport services, only show airport-related cabs
        else if (serviceType === "airport") {
          filteredCabs = filteredCabs.filter(
            (cab) =>
              cab.isAirportTransfer ||
              !cab.serviceType ||
              cab.serviceType === "airport",
          );
        }
        // For rental services, only show rental-related cabs
        else if (serviceType === "rental") {
          filteredCabs = filteredCabs.filter(
            (cab) =>
              cab.isRentalPackage ||
              !cab.serviceType ||
              cab.serviceType === "rental",
          );
        }

        // 3. Set cab options
        console.log("Setting cabOptions:", filteredCabs);
        console.log("Cab options length:", filteredCabs.length);

        // Don't override cabOptions if we already have popular/fixed route cabs
        if (!isPopularRoute && !isFixedRoute) {
          setCabOptions(filteredCabs);
        }
      } catch (error) {
        console.error("Error loading cab data:", error);
        setError("Failed to load cab options. Please try again.");

        // Try to fetch from local storage cache if API fails
        try {
          const cachedCabs = JSON.parse(
            localStorage.getItem("cabTypes") || "[]",
          );
          if (cachedCabs && cachedCabs.length > 0) {
            console.log("Using cached cab data from localStorage");
            setCabOptions(cachedCabs);
          } else {
            // No cached data available either
            setCabOptions([]);
            setError("No cab data available. Please try again later.");
          }
        } catch (cacheError) {
          console.error("Error reading from cache:", cacheError);
          setCabOptions([]);
          setError("Failed to load cab options. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Load data based on service type
    // Skip loadRealCabData if we already have popular route or fixed route cabs loaded
    if (!isPopularRoute && !isFixedRoute) {
      const dataPromises = [loadRealCabData()];

      // Only load additional data if needed for specific service types
      if (serviceType === "airport") {
        dataPromises.push(fetchAirports());
      } else if (serviceType === "rental") {
        dataPromises.push(fetchRentalPackages());
      }

      Promise.all(dataPromises);
    } else {
      // Still load airport/rental data even if we have fixed/popular routes
      const extraPromises = [];
      if (serviceType === "airport") {
        extraPromises.push(fetchAirports());
      } else if (serviceType === "rental") {
        extraPromises.push(fetchRentalPackages());
      }
      if (extraPromises.length > 0) {
        Promise.all(extraPromises);
      }
    }
  }, [fromCity, toCity, journeyType, tripType, dateStr, timeStr, returnDateStr, returnTimeStr, serviceType, transferDirection, airportCode, packageName, viaCitiesParam, navigate]);

  // Recalculate prices when distance changes (for airport transfers with dynamic pricing)
  useEffect(() => {
    if (distance !== null && cabOptions.length > 0) {
      const prices = {};
      cabOptions.forEach((cab) => {
        prices[cab._id] = getCabPrice(cab);
      });
      setCabPrices(prices);

      // Log the updated pricing
      if (!isPopularRoute && !isFixedRoute && distance > 0) {
        console.log("💰 PRICING UPDATED: Distance-based pricing recalculated");
        console.log("Distance used:", distance, "km");
        console.log("Updated prices:", prices);
      }
    }
  }, [distance, cabOptions.length]); // Recalculate when distance or cab count changes

  useEffect(() => {
    if (selectedCab) {
      document.body.classList.add("has-selected-cab");
    } else {
      document.body.classList.remove("has-selected-cab");
    }
    return () => {
      document.body.classList.remove("has-selected-cab");
    };
  }, [selectedCab]);

  // Handle cab selection - directly proceed to booking without extra confirmation bar
  const handleSelectCab = (cab) => {
    setSelectedCab(cab);
    handleProceedBooking(cab);
  };

  // Handle booking button click
  const handleBookNow = () => {
    if (!selectedCab) {
      return;
    }
    handleProceedBooking(selectedCab);
  };

  // Handle proceeding after cab selection / date/time selection
  const handleProceedBooking = (cabParam) => {
    const cabToBook = cabParam || selectedCab;
    console.log("🚗 Direct to Booking clicked for cab:", cabToBook?.name);

    // First validate the time inputs
    const validation = validateTripTimes();
    if (!validation.valid) {
      // If validation fails, keep the modal open and show error
      toast.error(validation.error);
      return;
    }

    // Close the modal
    setShowDateModal(false);

    // CRITICAL: Check if a cab is selected before proceeding
    if (!cabToBook) {
      console.warn("⚠️ Cannot proceed: No cab selected");
      toast.error("Please select a cab first");
      return;
    }

    // Check if user is logged in - PRIMARILY using localStorage/direct token check (most reliable)
    // since Redux state might not be properly initialized on some page navigations
    let isLoggedIn = false;
    let userInfo = user || {};

    // For regular user bookings - Check for token in localStorage FIRST (most reliable)
    const tokenExists = !!localStorage.getItem("token");
    const reduxUserExists = !!user;

    console.log("🔐 Authentication check:", {
      tokenInLocalStorage: tokenExists,
      reduxUser: reduxUserExists,
      userToken: user?.token ? "exists" : "missing",
      isCorporateBooking: isCorporateBookingFlow
    });

    // User is logged in if token exists in localStorage OR Redux state has user
    isLoggedIn = tokenExists || reduxUserExists;

    // Try to get user info from Redux first, fallback to localStorage
    if (!isLoggedIn) {
      // Double-check localStorage as backup
      try {
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (localUser && localUser.token) {
          isLoggedIn = true;
          userInfo = localUser;
          console.log("✅ Found auth in localStorage (backup check)");
        } else if (localUser && localUser.phone) {
          // Even if token field is missing, if we have phone number, user likely logged in
          isLoggedIn = !!localStorage.getItem("token");
          userInfo = localUser;
        }
      } catch (e) {
        console.error("❌ Error checking local storage auth:", e);
      }
    } else if (reduxUserExists) {
      userInfo = user;
    } else {
      // Token exists but Redux state is empty - get user info from localStorage
      try {
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        userInfo = localUser;
      } catch (e) {
        console.error("❌ Error getting user info from localStorage:", e);
      }
    }

    // For corporate bookings, also accept corporateToken if no regular user auth
    if (isCorporateBookingFlow && !isLoggedIn) {
      isLoggedIn = !!localStorage.getItem("corporateToken");
      console.log("🔐 Corporate token fallback check:", {
        corporateToken: isLoggedIn ? "exists" : "missing"
      });
    }

    // Construct query parameters to pass to the next page
    const params = new URLSearchParams();
    params.append("from", fromCity);
    params.append("to", toCity);
    params.append("pickupAddress", fromCity);
    // For rental services, dropAddress should be the same as pickupAddress
    // since the car is picked up and dropped off in the same city
    const dropAddressValue = serviceType === "rental" ? fromCity : toCity;
    params.append("dropAddress", dropAddressValue);
    params.append("cabId", cabToBook?._id || "");
    params.append("cabName", cabToBook?.name || "");
    params.append("imageUrl", cabToBook?.imageUrl || "");
    params.append("tripType", tripType);

    // Add user details for pre-filling the next form
    if (user?.name || user?.fullName) {
      params.append("name", user.name || user.fullName);
    }
    if (user?.phone || user?.mobile) {
      params.append("mobile", user.phone || user.mobile);
    }
    if (user?.email) {
      params.append("email", user.email);
    }
    if (user?.gender) {
      params.append("gender", user.gender);
    }

    // Add via cities if present
    if (viaCities.length > 0) {
      params.append("via", viaCities.join("|"));
      console.log("🛣��� Adding via cities to navigation:", viaCities.join(","));
    }

    // Preserve service-specific parameters for rental and airport services
    if (serviceType === "rental") {
      params.append("service", "rental");
      if (packageName) {
        params.append("package", packageName);
      }
      console.log("🚗 Rental service parameters added:", { packageName });
    } else if (serviceType === "airport") {
      params.append("service", "airport");
      if (airportCode) {
        params.append("airport", airportCode);
      }
      if (transferDirection) {
        params.append("direction", transferDirection);
      }
      console.log("✈️ Airport service parameters added:", { airportCode, transferDirection });
    }

    // Extract the actual numeric price value - ensure we pass a number, not an object
    let fareAmount;

    // For rental services, use rental package price
    if (serviceType === "rental" && packageName) {
      console.log("Using rental package pricing for:", packageName);
      const selectedPackage = rentalPackages.find(
        (pkg) => pkg.name === decodeURIComponent(packageName),
      );
      if (selectedPackage) {
        let cabPrice = null;

        // Check service cities first (primary data structure)
        if (
          selectedPackage.serviceCities &&
          selectedPackage.serviceCities.length > 0
        ) {
          for (const serviceCity of selectedPackage.serviceCities) {
            if (
              serviceCity.cabPrices &&
              cabToBook.name &&
              serviceCity.cabPrices[cabToBook.name]
            ) {
              cabPrice = serviceCity.cabPrices[cabToBook.name];
              console.log(
                `Found rental package price from service city: ₹${cabPrice} for ${cabToBook.name}`,
              );
              break;
            }
          }
        }

        // Fallback to legacy cab type pricing if no service city price found
        if (
          !cabPrice &&
          selectedPackage.cabTypePrices &&
          selectedPackage.cabTypePrices.length > 0
        ) {
          const cabTypePricing = selectedPackage.cabTypePrices.find(
            (cp) =>
              (cabToBook.name && cp.cabTypeId?.name === cabToBook.name) ||
              cp.cabTypeId?._id === cabToBook?._id ||
              cp.cabTypeId === cabToBook?._id ||
              cp.cabTypeId === cabToBook.name,
          );
          if (cabTypePricing) {
            cabPrice = cabTypePricing.price;
            console.log(
              `Found rental package price from cab types: ₹${cabPrice} for ${cabToBook.name}`,
            );
          }
        }

        if (cabPrice) {
          fareAmount = cabPrice;
        } else {
          console.warn(
            "No rental price found for this cab type, using base price",
          );
          fareAmount = selectedPackage.basePrice || 1000;
        }
      } else {
        console.warn("Rental package not found, using fallback price");
        fareAmount = 1000; // Fallback price for rental
      }
    }
    // For airport services, use airport-specific pricing
    else if (serviceType === "airport" && airportCode) {
      console.log("Using airport pricing for:", airportCode);
      const selectedAirport = airports.find(
        (airport) => airport.code === airportCode,
      );
      if (selectedAirport) {
        let airportPrice = null;

        // First check service cities with cab-specific pricing
        if (
          selectedAirport.serviceCities &&
          selectedAirport.serviceCities.length > 0
        ) {
          for (const serviceCity of selectedAirport.serviceCities) {
            if (
              serviceCity.cabPrices &&
              cabToBook.name &&
              serviceCity.cabPrices[cabToBook.name]
            ) {
              airportPrice = serviceCity.cabPrices[cabToBook.name];
              console.log(
                `Found airport price in service cities: ₹${airportPrice} for ${cabToBook.name}`,
              );
              break;
            }
          }
        }

        // Fallback to legacy cab type pricing
        if (
          !airportPrice &&
          selectedAirport.cabTypePrices &&
          selectedAirport.cabTypePrices.length > 0
        ) {
          const cabTypePricing = selectedAirport.cabTypePrices.find(
            (cp) =>
              (cabToBook.name && cp.cabTypeId?.name === cabToBook.name) ||
              cp.cabTypeId === cabToBook?._id,
          );
          if (cabTypePricing) {
            airportPrice = cabTypePricing.price;
            console.log(
              `Found airport price in cab types: ₹${airportPrice} for ${cabToBook.name}`,
            );
          }
        }

        if (airportPrice) {
          fareAmount = airportPrice;
        } else {
          console.warn(
            "No airport price found for this cab type, using fallback",
          );
          fareAmount = 2000; // Fallback price for airport transfers
        }
      } else {
        console.warn("Airport not found, using fallback price");
        fareAmount = 2000; // Fallback price for airport transfers
      }
    }
    // Don't override rental or airport pricing
    else if (
      cabPrices[cabToBook._id] &&
      cabPrices[cabToBook._id].originalPrice
    ) {
      // Get the numeric value from the price object
      fareAmount = cabPrices[cabToBook._id].originalPrice;
    } else if (cabToBook.isFixedRoute && cabToBook.price) {
      // For fixed routes, use the fixed price
      fareAmount = cabToBook.price;
    } else {
      // Calculate dynamic price based on distance and extract the totalPrice
      const priceInfo = calculateDynamicPrice(cabToBook);
      fareAmount = priceInfo.totalPrice || 0;
    }

    // Ensure fareAmount is a number
    fareAmount = Number(fareAmount) || 0;

    // Ensure we have a realistic minimum fare for long distances
    const roundedDistance = Math.round(distance);
    if (roundedDistance > 100 && fareAmount < 1000) {
      // Apply a realistic per-km rate for long journeys (₹12-15 per km is typical)
      const ratePerKm = cabToBook.category?.toLowerCase().includes("luxury")
        ? 15
        : 12;
      fareAmount = Math.round(roundedDistance * ratePerKm);
      console.log(
        `Applying realistic fare calculation: ${roundedDistance}km × ₹${ratePerKm}/km = ₹${fareAmount}`,
      );
    }

    params.append("date", pickupDate);
    params.append("time", pickupTime);
    params.append("amount", fareAmount);
    params.append("distance", Math.round(distance));

    // Set pickup location based on service type
    if (serviceType === "rental") {
      params.append("pickupLocation", fromCity);
    } else if (cabToBook.vehicleLocation) {
      params.append("pickupLocation", cabToBook.vehicleLocation);
    } else {
      params.append("pickupLocation", fromCity);
    }

    // Add user mobile number / name / email if available - for prefilling in the next form
    const incomingPhone = queryParams.get("mobile") || queryParams.get("phone");
    const incomingName = queryParams.get("name");
    const incomingEmail = queryParams.get("email");

    if (incomingPhone) {
      params.append("mobile", incomingPhone);
    } else if (isLoggedIn && userInfo?.phone) {
      params.append("mobile", userInfo.phone);
    }

    if (incomingName) {
      params.append("name", incomingName);
    } else if (isLoggedIn && (userInfo?.name || userInfo?.fullName)) {
      params.append("name", userInfo.name || userInfo.fullName);
    }

    if (incomingEmail) {
      params.append("email", incomingEmail);
    } else if (isLoggedIn && userInfo?.email) {
      params.append("email", userInfo.email);
    }

    // Use the collected date/time values
    if (tripType === "roundTrip") {
      params.append("returnDate", returnDate);
      params.append("returnTime", returnTime);
    }

    // Store comprehensive booking intent data in local storage for login recovery
    const bookingIntent = {
      from: fromCity,
      to: toCity,
      cabId: cabToBook?._id || "",
      cabName: cabToBook?.name || "",
      type: tripType,
      serviceType: serviceType,
      packageName: packageName,
      airportCode: airportCode,
      transferDirection: transferDirection,
      viaCities: viaCities,
      amount: fareAmount, // Use the properly calculated fare amount
      distance: distance,
      pickupLocation: cabToBook?.vehicleLocation || fromCity,
      date: pickupDate,
      time: pickupTime,
      returnDate: returnDate || "",
      returnTime: returnTime || "",
      timestamp: new Date().toISOString(),
    };

    console.log("💾 Storing booking intent:", bookingIntent);

    // Store selected cab data in local storage for persistence
    localStorage.setItem(
      "selectedCabData",
      JSON.stringify({
        id: cabToBook?._id,
        name: cabToBook?.name,
        basePrice: cabToBook?.basePrice,
        imageUrl: cabToBook?.imageUrl,
      }),
    );

    // Store booking intent for recovery after login
    localStorage.setItem("bookingIntent", JSON.stringify(bookingIntent));

    // Only add corporate flag if user is authenticated with corporateToken (not regular user)
    const hasCorporateToken = !!localStorage.getItem("corporateToken");
    if (isCorporateBookingFlow && hasCorporateToken) {
      params.append("corporate", "1");
    }

    // Track lead upon cab selection
    try {
      trackAbandonedBooking({
        pickupLocation: { address: cabToBook?.vehicleLocation || fromCity || "" },
        dropLocation: { address: toCity || "" },
        tripType: tripType || "one-way",
        pickupDate: pickupDate || "",
        pickupTime: pickupTime || "",
        returnDate: returnDate || "",
        returnTime: returnTime || "",
        phone: userInfo?.phone || "",
        selectedCab: {
          cabId: cabToBook?._id || "",
          cabName: cabToBook?.name || "",
          cabType: cabToBook?.category || cabToBook?.type || "",
          price: fareAmount || 0,
        },
        estimatedPrice: fareAmount || 0,
        distance: distance ? `${distance}` : "",
        exitPage: "/booking/customer-details",
      });
    } catch (trackErr) {
      console.warn("Lead tracking silent error:", trackErr);
    }

    // Check if not logged in and redirect accordingly
    if (!isLoggedIn) {
      console.log("🔒 User not logged in, redirecting to login");

      // Store current selection in localStorage for returning after login
      localStorage.setItem(
        "selectedCabBeforeLogin",
        JSON.stringify({
          id: cabToBook?._id || "",
          name: cabToBook?.name || "",
          fromCity: fromCity,
          toCity: toCity,
          fromLocation: fromLocation,
          toLocation: toLocation,
          distance: distance,
          date: pickupDate || dateStr,
          time: pickupTime || time,
          timestamp: new Date().toISOString(),
        }),
      );

      // Construct the complete booking URL with all parameters
      const bookingUrl = `/booking/customer-details?${params.toString()}`;

      if (isCorporateBookingFlow) {
        console.log("🏢 Redirecting to corporate login");
        toast.info("Please log in to continue with your booking");
        navigate("/corporate/login");
        return;
      }

      // If not logged in, redirect to login with complete booking URL as redirect parameter
      const loginUrl = `/login?redirect=${encodeURIComponent(bookingUrl)}`;
      console.log("🔄 Login redirect URL:", loginUrl);

      toast.info("Please log in to continue with your booking");
      navigate(loginUrl);
      return;
    }

    // User is logged in, proceed to booking
    const bookingUrl = `/booking/customer-details?${params.toString()}`;
    console.log("🔗 Generated booking URL:", bookingUrl);
    console.log("📋 URL Parameters:", Object.fromEntries(params.entries()));
    console.log("✅ User is logged in, proceeding directly to booking");
    navigate(bookingUrl);
  };

  if (isLoading && cabOptions.length === 0 && !isSameCityOutstation) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Finding the best cabs for you...</p>
        </div>
      </div>
    );
  }

  // Defensive null checks for arrays
  const safeCabOptions = Array.isArray(cabOptions) ? cabOptions.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id) : [];
  const safeRentalPackages = Array.isArray(rentalPackages) ? rentalPackages : [];
  const safeAirports = Array.isArray(airports) ? airports : [];

  // Extract unique filter options from cab data
  const uniqueCabTypes = [...new Set(safeCabOptions.map(cab => cab.category || cab.name?.split(' ')[0] || "").filter(Boolean))].sort();
  const uniqueFuelTypes = [...new Set(safeCabOptions.map(cab => cab.fuelType || "Petrol").filter(Boolean))].sort();
  const uniqueModelTypes = [...new Set(safeCabOptions.map(cab => cab.model || cab.description || "").filter(Boolean))].sort();

  // Filter cabs based on selected filters and sorting criteria
  const getCabPriceValue = (cab) => {
    const priceInfo = getCabPrice(cab);
    return priceInfo?.originalPrice || cab.price || 0;
  };

  const filteredCabs = safeCabOptions.filter(cab => {
    const cabType = cab.category || cab.name?.split(' ')[0] || "";
    const fuelType = cab.fuelType || "Petrol";
    const modelType = cab.model || cab.description || "";

    const matchesCabType = selectedCabTypes.length === 0 || selectedCabTypes.includes(cabType);
    const matchesFuelType = selectedFuelTypes.length === 0 || selectedFuelTypes.includes(fuelType);
    const matchesModelType = selectedModelTypes.length === 0 || selectedModelTypes.includes(modelType);

    return matchesCabType && matchesFuelType && matchesModelType;
  }).sort((a, b) => {
    if (sortBy === "price_low_high") {
      return getCabPriceValue(a) - getCabPriceValue(b);
    } else if (sortBy === "price_high_low") {
      return getCabPriceValue(b) - getCabPriceValue(a);
    }
    return 0;
  });

  // Toggle filter function
  const toggleFilter = (filterType, value) => {
    if (filterType === "cabType") {
      setSelectedCabTypes(prev =>
        prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
      );
    } else if (filterType === "fuelType") {
      setSelectedFuelTypes(prev =>
        prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
      );
    } else if (filterType === "modelType") {
      setSelectedModelTypes(prev =>
        prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
      );
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCabTypes([]);
    setSelectedFuelTypes([]);
    setSelectedModelTypes([]);
  };

  return (
    <div className="max-w-[1180px] mx-auto py-0 md:pt-4 md:pb-12 px-0 md:px-6 min-h-screen bg-gray-50 md:bg-transparent pb-20 md:pb-8">
      {/* Date/Time Selection Modal */}
      {showDateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b bg-white">
              <h3 className="text-lg font-bold text-gray-900">
                {isModifyingSearch ? "Modify Travel Details" : "Trip Details"}
              </h3>
              <button
                onClick={() => setShowDateModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-5 overflow-y-auto custom-scrollbar">

              {/* Current trip summary */}
              <div className="bg-blue-50/50 p-4 rounded-xl mb-4 border border-blue-100">
                {serviceType === "rental" ? (
                  <div>
                    <div className="flex items-center mb-2">
                      <FaGift className="text-blue-600 mr-2" />
                      <span className="font-medium">
                        Local Rental in {fromCity}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="inline-block">
                        Package: {packageName || "4 Hours/40 KM"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center mb-2">
                      <FaMapMarkerAlt className="text-blue-600 mr-2" />
                      <span className="font-medium">{fromCity}</span>
                      <FaArrowRight className="mx-2 text-gray-500" />
                      <span className="font-medium">{toCity}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="inline-block mr-3">
                        {tripType === "roundTrip"
                          ? "Round Trip"
                          : viaCities.length > 0
                            ? "Multi-City"
                            : "One Way"}
                      </span>
                      <span className="inline-block">
                        {distance ? Math.round(distance) : 0} km
                      </span>
                    </div>
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Pickup Date:</span>{" "}
                  {formatDateDDMMYYYY(pickupDate)}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Pickup Time:</span>{" "}
                  {formatTime12Hour(pickupTime)}
                </div>
                {tripType === "roundTrip" && (
                  <>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Return Date:</span>{" "}
                      {formatDateDDMMYYYY(returnDate)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Return Time:</span>{" "}
                      {formatTime12Hour(returnTime)}
                    </div>
                  </>
                )}
                <div className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Selected Cab:</span>{" "}
                  {selectedCab?.name}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wider">
                  Modify Travel Details
                </h4>
                {/* Pickup Date and Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FaCalendarAlt className="inline mr-2 text-blue-600" />
                    Pickup Date
                  </label>
                  <input
                    type="date"
                    value={pickupDate || getLocalDateString(new Date())}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min={getLocalDateString(new Date())}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FaClock className="inline mr-2 text-blue-600" />
                    Pickup Time
                  </label>
                  <TimePicker12Hour
                    value={
                      pickupTime ||
                      (() => {
                        const now = new Date();
                        return `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
                      })()
                    }
                    onChange={(val) => setPickupTime(val)}
                  />
                </div>

                {/* Return Date and Time (only for round trip) */}
                {tripType === "roundTrip" && (
                  <>
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FaCalendarAlt className="inline mr-2 text-blue-600" />
                        Return Date
                      </label>
                      <input
                        type="date"
                        value={
                          returnDate || getLocalDateString(new Date())
                        }
                        onChange={(e) => setReturnDate(e.target.value)}
                        className="p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min={pickupDate || getLocalDateString(new Date())}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FaClock className="inline mr-2 text-blue-600" />
                        Return Time
                      </label>
                      <TimePicker12Hour
                        value={returnTime || "10:00"}
                        onChange={(val) => setReturnTime(val)}
                      />
                    </div>
                  </>
                )}

                {timeValidationError && (
                  <div className="text-red-500 text-sm mt-2">
                    {timeValidationError}
                  </div>
                )}

                <div className="flex gap-3 mt-6 sticky bottom-0 bg-white pt-2">
                  <button
                    onClick={() => setShowDateModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={isModifyingSearch ? handleTimeUpdate : handleProceedBooking}
                    className="flex-2 bg-blue-600 text-white py-2.5 px-6 rounded-xl font-semibold hover:bg-blue-700 shadow-md shadow-blue-200 transition-all active:scale-[0.98]"
                  >
                    {isModifyingSearch ? "Update Search" : "Continue to Booking"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Route Popup Modal - Shows full route with via cities */}
      {showRoutePopup && viaCities.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center">
                <FaRoute className="mr-2" />
                Full Route Path
              </h3>
              <button
                onClick={() => setShowRoutePopup(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-blue-600 mb-4">
                {Math.round(distance || 0)} km • {viaCities.length + 1} segments
              </p>
              <div className="space-y-4">
                {/* Starting point */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                    <FaMapMarkerAlt className="text-green-600" />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">{fromCity}</div>
                    <div className="text-xs text-gray-500">Starting Point</div>
                  </div>
                </div>

                {/* Via cities */}
                {viaCities.map((viaCity, index) => (
                  <Fragment key={index}>
                    <div className="ml-4 flex items-center text-xs text-gray-400">
                      <FaArrowDown className="mr-2" />
                      <span>{Math.round((distance || 0) / (viaCities.length + 1))} km</span>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-yellow-100 rounded-full">
                        <FaMapMarkerAlt className="text-yellow-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">{viaCity}</div>
                        <div className="text-xs text-blue-600">Via Point {index + 1}</div>
                      </div>
                    </div>
                  </Fragment>
                ))}

                {/* Final segment */}
                <div className="ml-4 flex items-center text-xs text-gray-400">
                  <FaArrowDown className="mr-2" />
                  <span>{Math.round((distance || 0) / (viaCities.length + 1))} km</span>
                </div>

                {/* Destination */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-100 rounded-full">
                    <FaMapMarkerAlt className="text-red-600" />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">{toCity}</div>
                    <div className="text-xs text-gray-500">Destination</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowRoutePopup(false)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* MOBILE HEADER (MakeMyTrip Style) - FIXED to viewport top */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-md">
        <div className="flex items-center px-3 py-1.5 gap-1.5">
          <button 
            onClick={() => navigate(-1)} 
            className="text-white hover:text-gray-200 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-bold !text-[13px] leading-tight truncate">
              {serviceType === "rental" ? (
                `Local Rental in ${extractCityAndState(fromCity).city || fromCity}`
              ) : (
                `${extractCityAndState(fromCity).city || fromCity} to ${extractCityAndState(toCity).city || toCity}`
              )}
            </p>
            <p className="text-[10px] text-blue-200 font-semibold mt-0.5 truncate">
              {formatDateDDMMYYYY(dateStr)} • {timeStr}
              {serviceType === "rental" && packageName ? ` • ${packageName}` : ""}
              {tripType === "roundTrip" ? " • Round Trip" : ""}
            </p>
          </div>
          <button 
            onClick={() => {
              setIsModifyingSearch(true);
              setShowDateModal(true);
            }} 
            className="border border-blue-400/50 bg-blue-800/40 px-2.5 py-1 rounded-full text-[11px] font-bold text-white active:scale-95 transition-all flex-shrink-0 whitespace-nowrap"
          >
            Modify
          </button>
        </div>
      </div>

      {/* MOBILE HORIZONTAL FILTERS - FIXED below the header */}
      {!isSameCityOutstation && (
        <div className="md:hidden fixed top-[38px] left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto whitespace-nowrap py-1.5 px-4 flex items-center gap-2 scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Filters:</span>
        
        {uniqueCabTypes.map((type) => {
          const isActive = selectedCabTypes.includes(type);
          return (
            <button
              key={type}
              onClick={() => toggleFilter("cabType", type)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border ${
                isActive
                  ? "bg-blue-50 border-blue-600 text-blue-600 shadow-sm"
                  : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {type} {isActive && "✓"}
            </button>
          );
        })}

        {uniqueFuelTypes.map((fuel) => {
          const isActive = selectedFuelTypes.includes(fuel);
          return (
            <button
              key={fuel}
              onClick={() => toggleFilter("fuelType", fuel)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border ${
                isActive
                  ? "bg-blue-50 border-blue-600 text-blue-600 shadow-sm"
                  : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {fuel} {isActive && "✓"}
            </button>
          );
        })}

        {(selectedCabTypes.length > 0 || selectedFuelTypes.length > 0 || selectedModelTypes.length > 0) && (
          <button
            onClick={clearAllFilters}
            className="px-3 py-1 rounded-full text-xs font-bold bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 transition-all"
          >
            Clear All
          </button>
        )}
      </div>
      )}

      {/* MOBILE SPACER: pushes content below the fixed bars */}
      <div className="md:hidden" style={{ height: isSameCityOutstation ? '40px' : '74px' }}></div>

      {/* DESKTOP SEARCH BAR (Goibibo Style) */}
      <div className="hidden md:block mb-8" ref={barContainerRef}>
        <div className="bg-gradient-to-r from-[#f26a21] via-[#f36c24] to-[#ff7a36] rounded-xl p-2.5 sm:p-3 shadow-md relative">
          <div className="flex items-center justify-between gap-1.5 md:gap-2 overflow-visible py-0.5 w-full">
            {/* TRIP TYPE */}
            <div className="relative shrink-0">
              <div
                onClick={() => {
                  setShowTripTypeDropdown(!showTripTypeDropdown);
                  setShowFromDropdown(false);
                  setShowToDropdown(false);
                  setShowPickupDateDropdown(false);
                  setShowPickupTimeDropdown(false);
                  setShowReturnDateDropdown(false);
                  setShowReturnTimeDropdown(false);
                }}
                className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all w-[130px] lg:w-[145px] h-[48px]"
              >
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                  TRIP TYPE
                </span>
                <div className="flex items-center justify-between gap-1 text-[12px] font-bold text-gray-900 leading-tight">
                  <span className="truncate">
                    {serviceType === "rental"
                      ? "Rentals"
                      : serviceType === "airport"
                      ? "Airport Cabs"
                      : selectedTripType === "roundTrip"
                      ? "Outstation Round-Trip"
                      : selectedStops.length > 0
                      ? "Outstation Multi-City"
                      : "Outstation One-Way"}
                  </span>
                  <FaChevronDown
                    className={`text-[10px] text-gray-600 transition-transform duration-200 shrink-0 ${
                      showTripTypeDropdown ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>

              {/* Trip type dropdown (Goibibo Style) */}
              {showTripTypeDropdown && (
                <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 py-1.5 z-50 min-w-[270px]">
                  <div
                    onClick={() => handleTripTypeChange("oneWay")}
                    className={`px-4 py-2.5 hover:bg-orange-50 cursor-pointer transition-colors border-b border-gray-100 ${
                      selectedTripType === "oneWay" && !serviceType ? "bg-orange-50/70" : ""
                    }`}
                  >
                    <div className="text-sm font-bold text-gray-900">Outstation One-Way</div>
                    <div className="text-xs text-gray-500 mt-0.5">Drop off that includes one lunch stop</div>
                  </div>

                  <div
                    onClick={() => handleTripTypeChange("roundTrip")}
                    className={`px-4 py-2.5 hover:bg-orange-50 cursor-pointer transition-colors border-b border-gray-100 ${
                      selectedTripType === "roundTrip" && !serviceType ? "bg-orange-50/70" : ""
                    }`}
                  >
                    <div className="text-sm font-bold text-gray-900">Outstation Round-Trip</div>
                    <div className="text-xs text-gray-500 mt-0.5">Return trip that includes mutiple stops</div>
                  </div>

                  <div
                    onClick={() => handleTripTypeChange("airport")}
                    className={`px-4 py-2.5 hover:bg-orange-50 cursor-pointer transition-colors border-b border-gray-100 ${
                      serviceType === "airport" ? "bg-orange-50/70" : ""
                    }`}
                  >
                    <div className="text-sm font-bold text-gray-900">Airport Cabs</div>
                    <div className="text-xs text-gray-500 mt-0.5">Pre-book airport pickup & drop</div>
                  </div>

                  <div
                    onClick={() => handleTripTypeChange("rental")}
                    className={`px-4 py-2.5 hover:bg-orange-50 cursor-pointer transition-colors ${
                      serviceType === "rental" ? "bg-orange-50/70" : ""
                    }`}
                  >
                    <div className="text-sm font-bold text-gray-900">Rentals</div>
                    <div className="text-xs text-gray-500 mt-0.5">Car rental within the city</div>
                  </div>
                </div>
              )}
            </div>

            {/* FROM (flex-1 to fill the bar space) */}
            <div className="relative flex-1 min-w-[120px]">
              <div
                onClick={() => {
                  setShowFromDropdown(!showFromDropdown);
                  setShowToDropdown(false);
                  setShowTripTypeDropdown(false);
                  setShowPickupDateDropdown(false);
                  setShowPickupTimeDropdown(false);
                  setShowReturnDateDropdown(false);
                  setShowReturnTimeDropdown(false);
                  if (!showFromDropdown) {
                    fetchLocationPredictions(fromSearchQuery, setFromSuggestions);
                  }
                }}
                className="bg-white rounded-lg px-3 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all h-[48px] min-w-0"
                title={selectedFrom || fromLocation || fromCity}
              >
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                  FROM
                </span>
                <span className="text-[12px] lg:text-[13px] font-bold text-gray-900 leading-tight truncate">
                  {selectedFrom || fromLocation || fromCity}
                </span>
              </div>

              {/* FROM Dropdown (Goibibo Style search with 2-line format) */}
              {showFromDropdown && (
                <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 w-[300px] sm:w-[350px] overflow-hidden animate-fadeIn text-left">
                  <div className="flex items-center px-3.5 py-2.5 border-b border-gray-200 gap-2.5 bg-gray-50/50">
                    <FaSearch className="text-gray-400 text-xs shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      value={fromSearchQuery}
                      onChange={(e) => handleFromSearchChange(e.target.value)}
                      placeholder="From"
                      className="w-full text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
                    />
                    {fromSearchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setFromSearchQuery("");
                          fetchLocationPredictions("", setFromSuggestions);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes className="text-xs" />
                      </button>
                    )}
                  </div>

                  <div className="max-h-[260px] overflow-y-auto custom-scrollbar">
                    <div className="px-3.5 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/60 border-b border-gray-100">
                      {fromSearchQuery.trim() ? "SEARCH RESULTS" : "POPULAR CITIES"}
                    </div>
                    {fromSuggestions.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSelectFrom(item.fullText)}
                        className="px-3.5 py-2.5 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                      >
                        <div className="text-xs font-bold text-gray-900 leading-tight">
                          {item.mainText}
                        </div>
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">
                          {item.secondaryText}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* TO (flex-1 to fill the bar space) */}
            <div className="relative flex-1 min-w-[120px]">
              <div
                onClick={() => {
                  setShowToDropdown(!showToDropdown);
                  setShowFromDropdown(false);
                  setShowTripTypeDropdown(false);
                  setShowPickupDateDropdown(false);
                  setShowPickupTimeDropdown(false);
                  setShowReturnDateDropdown(false);
                  setShowReturnTimeDropdown(false);
                  if (!showToDropdown) {
                    fetchLocationPredictions(toSearchQuery, setToSuggestions);
                  }
                }}
                className="bg-white rounded-lg px-3 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all h-[48px] min-w-0"
                title={serviceType === "rental" ? packageName || "4 Hours/40 KM" : selectedTo || toLocation || toCity}
              >
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                  {serviceType === "rental" ? "PACKAGE" : "TO"}
                </span>
                <span className="text-[12px] lg:text-[13px] font-bold text-gray-900 leading-tight truncate">
                  {serviceType === "rental" ? packageName || "4 Hours/40 KM" : selectedTo || toLocation || toCity}
                </span>
              </div>

              {/* TO Dropdown (Goibibo Style search with 2-line format) */}
              {showToDropdown && (
                <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 w-[300px] sm:w-[350px] overflow-hidden animate-fadeIn text-left">
                  <div className="flex items-center px-3.5 py-2.5 border-b border-gray-200 gap-2.5 bg-gray-50/50">
                    <FaSearch className="text-gray-400 text-xs shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      value={toSearchQuery}
                      onChange={(e) => handleToSearchChange(e.target.value)}
                      placeholder="To"
                      className="w-full text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
                    />
                    {toSearchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setToSearchQuery("");
                          fetchLocationPredictions("", setToSuggestions);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes className="text-xs" />
                      </button>
                    )}
                  </div>

                  <div className="max-h-[260px] overflow-y-auto custom-scrollbar">
                    <div className="px-3.5 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/60 border-b border-gray-100">
                      {toSearchQuery.trim() ? "SEARCH RESULTS" : "POPULAR CITIES"}
                    </div>
                    {toSuggestions.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSelectTo(item.fullText)}
                        className="px-3.5 py-2.5 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                      >
                        <div className="text-xs font-bold text-gray-900 leading-tight">
                          {item.mainText}
                        </div>
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">
                          {item.secondaryText}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ADD STOPS Button (Opens Outstation Trip Plan Modal) */}
            {serviceType !== "rental" && (
              <button
                type="button"
                onClick={openTripPlanModal}
                className="relative bg-white hover:bg-gray-50 rounded-lg px-2.5 sm:px-3 py-1.5 flex items-center justify-center cursor-pointer shadow-sm border border-transparent hover:border-orange-300 active:scale-95 transition-all shrink-0 h-[48px]"
                title="Add stops and plan trip"
              >
                <span className="text-[10px] lg:text-[11px] font-bold text-gray-700 whitespace-nowrap">
                  {selectedStops.length > 0 ? `+ STOPS (${selectedStops.length})` : "+ ADD STOPS"}
                </span>
                <span className="absolute -top-1.5 -right-1 bg-[#8c52ff] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full uppercase tracking-tighter leading-none shadow-sm">
                  new
                </span>
              </button>
            )}

            {/* DATE & TIME (Combined into 1 card for Pick-up & 1 card for Return in Round Trip to keep SEARCH button completely inside) */}
            {selectedTripType === "roundTrip" ? (
              <>
                {/* COMBINED PICK - UP CARD */}
                <div className="relative shrink-0">
                  <div
                    onClick={() => {
                      setShowPickupCombinedDropdown(!showPickupCombinedDropdown);
                      setShowReturnCombinedDropdown(false);
                      setShowFromDropdown(false);
                      setShowToDropdown(false);
                      setShowTripTypeDropdown(false);
                      setShowTripPlanModal(false);
                    }}
                    className="bg-white rounded-lg px-2 lg:px-2.5 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all w-[130px] lg:w-[145px] h-[48px]"
                  >
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                      PICK - UP
                    </span>
                    <span className="text-[11px] lg:text-[12px] font-bold text-gray-900 leading-tight whitespace-nowrap truncate">
                      {formatGoibiboShortDateTime(pickupDate, pickupTime)}
                    </span>
                  </div>

                  {/* Combined Pick-Up Dropdown */}
                  {showPickupCombinedDropdown && (
                    <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 p-3.5 z-50 min-w-[240px] animate-fadeIn text-left">
                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Pick-Up Date
                      </div>
                      <input
                        type="date"
                        min={getLocalDateString(new Date())}
                        value={pickupDate || getLocalDateString(new Date())}
                        onChange={(e) => {
                          setPickupDate(e.target.value);
                        }}
                        className="w-full border border-gray-300 rounded-lg p-2 text-xs font-semibold focus:ring-2 focus:ring-[#f26a21] focus:outline-none mb-3"
                      />

                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Pick-Up Time
                      </div>
                      <TimePicker12Hour
                        value={pickupTime || "10:00"}
                        onChange={(val) => setPickupTime(val)}
                      />
                    </div>
                  )}
                </div>

                {/* COMBINED RETURN CARD */}
                <div className="relative shrink-0">
                  <div
                    onClick={() => {
                      setShowReturnCombinedDropdown(!showReturnCombinedDropdown);
                      setShowPickupCombinedDropdown(false);
                      setShowFromDropdown(false);
                      setShowToDropdown(false);
                      setShowTripTypeDropdown(false);
                      setShowTripPlanModal(false);
                    }}
                    className="bg-white rounded-lg px-2 lg:px-2.5 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all w-[130px] lg:w-[145px] h-[48px]"
                  >
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                      RETURN
                    </span>
                    <span className="text-[11px] lg:text-[12px] font-bold text-gray-900 leading-tight whitespace-nowrap truncate">
                      {formatGoibiboShortDateTime(returnDate, returnTime)}
                    </span>
                  </div>

                  {/* Combined Return Dropdown */}
                  {showReturnCombinedDropdown && (
                    <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 p-3.5 z-50 min-w-[240px] animate-fadeIn text-left">
                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Return Date
                      </div>
                      <input
                        type="date"
                        min={pickupDate || getLocalDateString(new Date())}
                        value={returnDate || getLocalDateString(new Date())}
                        onChange={(e) => {
                          setReturnDate(e.target.value);
                        }}
                        className="w-full border border-gray-300 rounded-lg p-2 text-xs font-semibold focus:ring-2 focus:ring-[#f26a21] focus:outline-none mb-3"
                      />

                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Return Time
                      </div>
                      <TimePicker12Hour
                        value={returnTime || "12:00"}
                        onChange={(val) => setReturnTime(val)}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* PICK - UP DATE (Separated for One-Way) */}
                <div className="relative shrink-0">
                  <div
                    onClick={() => {
                      setShowPickupDateDropdown(!showPickupDateDropdown);
                      setShowPickupTimeDropdown(false);
                      setShowFromDropdown(false);
                      setShowToDropdown(false);
                      setShowTripTypeDropdown(false);
                      setShowReturnDateDropdown(false);
                      setShowReturnTimeDropdown(false);
                      setShowTripPlanModal(false);
                    }}
                    className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all w-[115px] lg:w-[125px] h-[48px]"
                  >
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                      PICK - UP DATE
                    </span>
                    <span className="text-[12px] font-bold text-gray-900 leading-tight whitespace-nowrap truncate">
                      {formatGoibiboDate(pickupDate)}
                    </span>
                  </div>

                  {/* Datepicker Popover */}
                  {showPickupDateDropdown && (
                    <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 z-50 min-w-[220px] text-left">
                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Select Pick-Up Date
                      </div>
                      <input
                        type="date"
                        min={getLocalDateString(new Date())}
                        value={pickupDate || getLocalDateString(new Date())}
                        onChange={(e) => {
                          setPickupDate(e.target.value);
                          setShowPickupDateDropdown(false);
                        }}
                        className="w-full border border-gray-300 rounded-lg p-2 text-xs font-semibold focus:ring-2 focus:ring-[#f26a21] focus:outline-none"
                      />
                    </div>
                  )}
                </div>

                {/* PICK - UP TIME (Separated for One-Way) */}
                <div className="relative shrink-0">
                  <div
                    onClick={() => {
                      setShowPickupTimeDropdown(!showPickupTimeDropdown);
                      setShowPickupDateDropdown(false);
                      setShowFromDropdown(false);
                      setShowToDropdown(false);
                      setShowTripTypeDropdown(false);
                      setShowReturnDateDropdown(false);
                      setShowReturnTimeDropdown(false);
                      setShowTripPlanModal(false);
                    }}
                    className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm border border-transparent hover:border-orange-300 flex flex-col justify-center cursor-pointer transition-all w-[85px] lg:w-[95px] h-[48px]"
                  >
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none mb-0.5">
                      PICK - UP TIME
                    </span>
                    <span className="text-[12px] font-bold text-gray-900 leading-tight whitespace-nowrap">
                      {formatTime12Hour(pickupTime)}
                    </span>
                  </div>

                  {/* Timepicker Popover */}
                  {showPickupTimeDropdown && (
                    <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 z-50 min-w-[200px] text-left">
                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Select Pick-Up Time
                      </div>
                      <TimePicker12Hour
                        value={pickupTime || "10:00"}
                        onChange={(val) => {
                          setPickupTime(val);
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* SEARCH BUTTON */}
            <button
              type="button"
              onClick={handleApplySearch}
              className="bg-white hover:bg-gray-50 text-[#0070e0] font-black text-xs lg:text-[13px] tracking-wider px-5 sm:px-6 py-2 rounded-lg shadow-sm border border-transparent hover:border-blue-200 active:scale-95 transition-all flex items-center justify-center shrink-0 cursor-pointer h-[48px] uppercase"
            >
              SEARCH
            </button>
          </div>

          {/* OUTSTATION TRIP PLAN POPOVER (Anchored directly below the bar, compact size with full Autocomplete matching Image 4) */}
          {showTripPlanModal && (
            <div
              className="absolute top-[calc(100%+8px)] left-2 sm:left-[60px] md:left-[120px] lg:left-[180px] z-50 bg-white rounded-2xl shadow-xl border border-gray-200 w-[380px] sm:w-[410px] max-w-[92vw] p-4 animate-fadeIn text-left overflow-visible"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Circular Close Button */}
              <button
                type="button"
                onClick={() => {
                  setShowTripPlanModal(false);
                  setModalActiveField(null);
                }}
                className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-500 hover:text-black hover:scale-105 active:scale-95 transition-all z-20"
                aria-label="Close"
              >
                <FaTimes className="text-[10px]" />
              </button>

              {/* Header */}
              <div className="flex items-center justify-between pb-2.5 border-b border-gray-100 pr-4 sm:pr-0">
                <h3 className="text-sm font-bold text-gray-900 tracking-tight">
                  Outstation Trip Plan
                </h3>

                {/* Segmented Trip Type Tabs */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPlanTripType("oneWay")}
                    className={`px-2.5 py-1 rounded-lg border transition-all text-left ${
                      planTripType === "oneWay"
                        ? "bg-[#eef6ff] border-blue-500 text-blue-600 shadow-xs"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <div className="text-[11px] font-bold leading-tight">One-way</div>
                    <div className="text-[9px] text-gray-500 leading-tight">Get dropped off</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPlanTripType("roundTrip")}
                    className={`px-2.5 py-1 rounded-lg border transition-all text-left ${
                      planTripType === "roundTrip"
                        ? "bg-[#eef6ff] border-blue-500 text-blue-600 shadow-xs"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <div className="text-[11px] font-bold leading-tight">Round Trip</div>
                    <div className="text-[9px] text-gray-500 leading-tight">Keep cab till return</div>
                  </button>
                </div>
              </div>

              {/* Body / Timeline */}
              <div className="py-3 space-y-2 overflow-visible">
                {/* FROM Card */}
                <div className={`relative border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-gray-300 transition-colors ${modalActiveField === 'from' ? 'z-30 ring-2 ring-orange-400' : 'z-10'}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-4 flex items-center justify-center text-gray-400">
                      <span className="inline-block w-2 h-2 rounded-full border-2 border-gray-400"></span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-0.5">
                        FROM
                      </div>
                      <input
                        type="text"
                        value={planFrom}
                        onFocus={() => {
                          setModalActiveField('from');
                          fetchLocationPredictions(planFrom, setModalSuggestions);
                        }}
                        onChange={(e) => {
                          setPlanFrom(e.target.value);
                          setModalActiveField('from');
                          fetchLocationPredictions(e.target.value, setModalSuggestions);
                        }}
                        placeholder="Enter pickup city or location"
                        className="w-full text-xs font-bold text-gray-800 focus:outline-none placeholder-gray-400 bg-transparent leading-tight"
                      />
                    </div>
                    {planFrom && (
                      <button
                        type="button"
                        onClick={() => {
                          setPlanFrom("");
                          fetchLocationPredictions("", setModalSuggestions);
                        }}
                        className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors shrink-0"
                      >
                        <FaTimes className="text-[10px]" />
                      </button>
                    )}
                  </div>

                  {/* Suggestions Popover for FROM matching Image 4 */}
                  {modalActiveField === 'from' && (
                    <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn max-h-[200px] overflow-y-auto custom-scrollbar">
                      <div className="px-3.5 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/60 border-b border-gray-100">
                        {planFrom && planFrom.trim().length > 0 ? "SEARCH RESULTS" : "POPULAR CITIES"}
                      </div>
                      {modalSuggestions.map((item, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            setPlanFrom(item.fullText);
                            setModalActiveField(null);
                          }}
                          className="px-3.5 py-2 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors text-left"
                        >
                          <div className="text-xs font-bold text-gray-900 leading-tight">
                            {item.mainText}
                          </div>
                          <div className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">
                            {item.secondaryText}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* STOPS LIST with full Autocomplete matching Image 4 */}
                {planStops.map((stop, idx) => (
                  <div
                    key={idx}
                    className={`relative border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-gray-300 transition-colors ${modalActiveField === `stop-${idx}` ? 'z-30 ring-2 ring-orange-400' : 'z-10'}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center text-[9px] font-bold text-gray-600 shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-0.5">
                          STOP {idx + 1}
                        </div>
                        <input
                          type="text"
                          value={stop}
                          onFocus={() => {
                            setModalActiveField(`stop-${idx}`);
                            fetchLocationPredictions(stop, setModalSuggestions);
                          }}
                          onChange={(e) => {
                            const newStops = [...planStops];
                            newStops[idx] = e.target.value;
                            setPlanStops(newStops);
                            setModalActiveField(`stop-${idx}`);
                            fetchLocationPredictions(e.target.value, setModalSuggestions);
                          }}
                          placeholder="Enter stop location"
                          className="w-full text-xs font-bold text-gray-800 focus:outline-none placeholder-gray-400 bg-transparent leading-tight"
                        />
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-400 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setPlanStops(planStops.filter((_, i) => i !== idx));
                            if (modalActiveField === `stop-${idx}`) setModalActiveField(null);
                          }}
                          className="p-0.5 hover:text-red-500 rounded-full transition-colors"
                          title="Remove stop"
                        >
                          <FaTimes className="text-[10px]" />
                        </button>
                      </div>
                    </div>

                    {/* Suggestions Popover for this STOP matching Image 4 */}
                    {modalActiveField === `stop-${idx}` && (
                      <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn max-h-[200px] overflow-y-auto custom-scrollbar">
                        <div className="px-3.5 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/60 border-b border-gray-100">
                          {stop && stop.trim().length > 0 ? "SEARCH RESULTS" : "POPULAR CITIES"}
                        </div>
                        {modalSuggestions.map((item, sIdx) => (
                          <div
                            key={sIdx}
                            onClick={() => {
                              const updated = [...planStops];
                              updated[idx] = item.fullText;
                              setPlanStops(updated);
                              setModalActiveField(null);
                            }}
                            className="px-3.5 py-2 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors text-left"
                          >
                            <div className="text-xs font-bold text-gray-900 leading-tight">
                              {item.mainText}
                            </div>
                            <div className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">
                              {item.secondaryText}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* TO Card with full Autocomplete matching Image 4 */}
                <div className={`relative border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-gray-300 transition-colors ${modalActiveField === 'to' ? 'z-30 ring-2 ring-orange-400' : 'z-10'}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-4 flex items-center justify-center text-gray-400">
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-500"></span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-0.5">
                        {planTo ? "TO" : "ENTER LOCATION HERE"}
                      </div>
                      <input
                        type="text"
                        value={planTo}
                        onFocus={() => {
                          setModalActiveField('to');
                          fetchLocationPredictions(planTo, setModalSuggestions);
                        }}
                        onChange={(e) => {
                          setPlanTo(e.target.value);
                          setModalActiveField('to');
                          fetchLocationPredictions(e.target.value, setModalSuggestions);
                        }}
                        placeholder="Enter destination city or location"
                        className="w-full text-xs font-bold text-gray-800 focus:outline-none placeholder-gray-400 bg-transparent leading-tight"
                      />
                    </div>
                    {planTo && (
                      <button
                        type="button"
                        onClick={() => {
                          setPlanTo("");
                          fetchLocationPredictions("", setModalSuggestions);
                        }}
                        className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors shrink-0"
                      >
                        <FaTimes className="text-[10px]" />
                      </button>
                    )}
                  </div>

                  {/* Suggestions Popover for TO matching Image 4 */}
                  {modalActiveField === 'to' && (
                    <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn max-h-[200px] overflow-y-auto custom-scrollbar">
                      <div className="px-3.5 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/60 border-b border-gray-100">
                        {planTo && planTo.trim().length > 0 ? "SEARCH RESULTS" : "POPULAR CITIES"}
                      </div>
                      {modalSuggestions.map((item, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            setPlanTo(item.fullText);
                            setModalActiveField(null);
                          }}
                          className="px-3.5 py-2 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors text-left"
                        >
                          <div className="text-xs font-bold text-gray-900 leading-tight">
                            {item.mainText}
                          </div>
                          <div className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">
                            {item.secondaryText}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* + Add a stop Button */}
                <div className="pt-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setPlanStops([...planStops, ""]);
                      setModalActiveField(`stop-${planStops.length}`);
                      fetchLocationPredictions("", setModalSuggestions);
                    }}
                    className="border border-blue-500 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1 active:scale-95"
                  >
                    <span>+ Add a stop</span>
                  </button>
                </div>
              </div>

              {/* Footer: Orange DONE Button */}
              <div className="pt-2.5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleApplyTripPlan}
                  className="w-full bg-[#f26a21] hover:bg-[#ea580c] text-white font-extrabold text-xs tracking-wider py-2.5 rounded-lg uppercase transition-all shadow-sm active:scale-[0.98]"
                >
                  DONE
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Route Details - Only show when expanded and via cities exist */}
      {isRouteExpanded && serviceType !== "rental" && viaCities.length > 0 && (
        <div className="mb-6 px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 flex items-center">
                <FaRoute className="mr-2" />
                Full Route Path
              </h3>
              <p className="text-sm text-blue-600 mt-1">
                {Math.round(distance)} km • {viaCities.length + 1} segments
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Starting point */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <FaMapMarkerAlt className="text-green-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {fromCity}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    <div>{Math.round(distance / (viaCities.length + 1))} km</div>
                    <div>₹{Math.round((distance / (viaCities.length + 1)) * (baseRate || 12))}</div>
                  </div>
                </div>

                {/* Via cities */}
                {viaCities.map((viaCity, index) => (
                  <Fragment key={index}>
                    <div className="ml-3 flex items-center text-xs text-gray-500">
                      <FaArrowDown className="mr-2" />
                      <span>
                        {Math.round(distance / (viaCities.length + 1))} km • ₹{baseRate || 12}/km
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        <FaMapMarkerAlt className="text-blue-500" />
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {viaCity}
                          <span className="ml-2 text-xs text-blue-600">(Via)</span>
                        </div>
                      </div>
                      {index < viaCities.length && (
                        <div className="text-right text-xs text-gray-600">
                          <div>{Math.round(distance / (viaCities.length + 1))} km</div>
                          <div>₹{Math.round((distance / (viaCities.length + 1)) * (baseRate || 12))}</div>
                        </div>
                      )}
                    </div>
                  </Fragment>
                ))}

                {/* Final segment to destination */}
                <div className="ml-3 flex items-center text-xs text-gray-500">
                  <FaArrowDown className="mr-2" />
                  <span>
                    {Math.round(distance / (viaCities.length + 1))} km • ₹{baseRate || 12}/km
                  </span>
                </div>

                {/* Destination */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <FaMapMarkerAlt className="text-red-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {toCity}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    <div>{Math.round(distance / (viaCities.length + 1))} km</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Benefits Banner */}
      {!isSameCityOutstation && (
        <div className="hidden md:block">
          <BenefitsBanner />
        </div>
      )}

      {/* Same-City Outstation Error State (Goibibo Style) */}
      {isSameCityOutstation ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-150 p-8 sm:p-12 md:p-16 text-center my-6 max-w-3xl mx-auto">
          {/* Nature Line Illustration matching Goibibo */}
          <div className="w-64 sm:w-80 h-36 mx-auto mb-6 flex items-center justify-center">
            <svg
              viewBox="0 0 320 160"
              className="w-full h-full"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Cloud */}
              <path
                d="M210 32 C210 24 218 18 228 18 C236 18 243 23 245 28 C249 26 254 27 256 30 C260 33 260 37 258 40 C261 42 262 45 261 48 C259 51 256 53 252 53 L210 53 C204 53 199 48 199 43 C199 37 204 32 210 32 Z"
                stroke="#1F2937"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="#FFFFFF"
              />
              {/* Birds */}
              <path
                d="M175 38 Q179 33 183 36 Q187 33 191 38"
                stroke="#1F2937"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M155 52 Q158 48 162 50 Q166 48 169 52"
                stroke="#1F2937"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Ground short dash lines */}
              <line x1="90" y1="118" x2="108" y2="118" stroke="#1F2937" strokeWidth="1.8" strokeLinecap="round" />
              <line x1="122" y1="118" x2="135" y2="118" stroke="#1F2937" strokeWidth="1.8" strokeLinecap="round" />
              <line x1="148" y1="126" x2="168" y2="126" stroke="#1F2937" strokeWidth="1.8" strokeLinecap="round" />
              <line x1="240" y1="132" x2="260" y2="132" stroke="#1F2937" strokeWidth="1.8" strokeLinecap="round" />
              {/* Main horizon line with curve */}
              <path
                d="M110 110 L175 110 C185 110 188 120 198 120 L250 120"
                stroke="#1F2937"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Left Tree */}
              <line x1="140" y1="92" x2="140" y2="110" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" />
              <path
                d="M140 76 C131 85 130 98 140 104 C150 98 149 85 140 76 Z"
                fill="#E2E8F0"
                stroke="#1F2937"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
              <line x1="140" y1="78" x2="140" y2="104" stroke="#1F2937" strokeWidth="1.4" />
              {/* Right Tree */}
              <line x1="220" y1="102" x2="220" y2="120" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" />
              <circle
                cx="220"
                cy="102"
                r="14"
                fill="#CBD5E1"
                stroke="#1F2937"
                strokeWidth="1.8"
              />
              <line x1="220" y1="92" x2="220" y2="112" stroke="#1F2937" strokeWidth="1.4" />
              <line x1="220" y1="104" x2="228" y2="99" stroke="#1F2937" strokeWidth="1.4" />
            </svg>
          </div>

          {/* Heading matching Goibibo screenshot */}
          <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight leading-snug">
            Dear customer,
            <br />
            {isMultiDaySameCity
              ? "We currently do not support multi-day trips in the same city"
              : "Pickup and destination cities cannot be the same"}
          </h2>

          <p className="text-base sm:text-lg font-bold text-gray-900 mt-4 sm:mt-5">
            Please try a valid search
          </p>

          {/* Action buttons */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setShowTripPlanModal(true);
                setModalActiveField("to");
              }}
              className="px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20 transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
            >
              Change Destination
            </button>
            <button
              type="button"
              onClick={() => {
                const rentalParams = new URLSearchParams();
                rentalParams.set("service", "rental");
                rentalParams.set("city", fromCity || "Rajkot");
                rentalParams.set("from", fromCity || "Rajkot");
                if (dateStr) rentalParams.set("date", dateStr);
                if (timeStr) rentalParams.set("time", timeStr);
                navigate(`/cabs?${rentalParams.toString()}`);
              }}
              className="px-6 py-2.5 rounded-xl font-bold text-xs tracking-wider border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all active:scale-95 cursor-pointer"
            >
              Need a local cab? View Hourly Rentals
            </button>
          </div>
        </div>
      ) : cabOptions.length === 0 ? (
        <div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-medium text-yellow-800">
              No cabs available
            </h3>
            <p className="text-yellow-600 mt-2">
              Sorry, we couldn't find any cabs for your selected route and date.
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Try Another Route
            </button>
          </div>
        </div>
      ) : null}

      {/* New Cab listing with filters */}
      {cabOptions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-2 md:mt-0">

          {/* LEFT FILTER SIDEBAR */}
          <div className="hidden md:block bg-white rounded-xl p-6 h-fit sticky top-8 border border-gray-300 shadow-md hover:shadow-lg hover:border-gray-400 transition-all duration-300">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-[#7A7A7A] -mx-6 px-6">
              <h3 className="font-bold text-lg text-gray-900">Filters</h3>
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-700 hover:text-gray-900 transition-colors"
              >
                CLEAR ALL
              </button>
            </div>

            {/* Cab Type Filter */}
            {uniqueCabTypes.length > 0 && (
              <div className="mb-6 pb-6 border-b border-[#7A7A7A]">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">Cab Type</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedCabTypes([])}
                      className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      CLEAR
                    </button>
                    <button
                      onClick={() => setIsCabTypeExpanded(!isCabTypeExpanded)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isCabTypeExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </button>
                  </div>
                </div>
                {isCabTypeExpanded && uniqueCabTypes.map((type) => {
                  const count = cabOptions.filter(cab => cab.category?.toLowerCase() === type.toLowerCase() || cab.name?.toLowerCase().includes(type.toLowerCase())).length;
                  return (
                    <label key={type} className="flex items-center justify-between gap-2 mb-2 text-gray-600 hover:text-gray-900 cursor-pointer transition-colors">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedCabTypes.includes(type)}
                          onChange={() => toggleFilter("cabType", type)}
                          className="accent-blue-500 cursor-pointer"
                        />
                        {type}
                      </div>
                      <span className="text-xs text-gray-400">({count})</span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Fuel Type Filter */}
            {uniqueFuelTypes.length > 0 && (
              <div className="mb-6 pb-6 border-b border-[#7A7A7A]">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">Fuel Type</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedFuelTypes([])}
                      className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      CLEAR
                    </button>
                    <button
                      onClick={() => setIsFuelTypeExpanded(!isFuelTypeExpanded)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isFuelTypeExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </button>
                  </div>
                </div>
                {isFuelTypeExpanded && uniqueFuelTypes.map((fuel) => {
                  const count = cabOptions.filter(cab => cab.fuelType?.toLowerCase() === fuel.toLowerCase()).length;
                  return (
                    <label key={fuel} className="flex items-center justify-between gap-2 mb-2 text-gray-600 hover:text-gray-900 cursor-pointer transition-colors">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedFuelTypes.includes(fuel)}
                          onChange={() => toggleFilter("fuelType", fuel)}
                          className="accent-blue-500 cursor-pointer"
                        />
                        {fuel}
                      </div>
                      <span className="text-xs text-gray-400">({count})</span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Model Type Filter */}
            {uniqueModelTypes.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">Model Type</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedModelTypes([])}
                      className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      CLEAR
                    </button>
                    <button
                      onClick={() => setIsModelTypeExpanded(!isModelTypeExpanded)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isModelTypeExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </button>
                  </div>
                </div>
                {isModelTypeExpanded && uniqueModelTypes.slice(0, 5).map((model) => {
                  const count = cabOptions.filter(cab => cab.model === model || cab.name === model).length;
                  return (
                    <label key={model} className="flex items-center justify-between gap-2 mb-2 text-gray-600 hover:text-gray-900 cursor-pointer transition-colors overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedModelTypes.includes(model)}
                          onChange={() => toggleFilter("modelType", model)}
                          className="accent-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <span className="truncate text-sm block max-w-[140px]" title={model}>{model}</span>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">({count})</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT CAB LIST */}
          <div className="col-span-1 md:col-span-3 space-y-4 px-4 pt-1 pb-4 md:px-0 md:py-0">
            {filteredCabs.length === 0 ? (
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <p className="text-gray-600 mb-4">No cabs match your selected filters</p>
                <button
                  onClick={clearAllFilters}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  // Find the cab with lowest price for "Best Value" badge
                  let bestValueCabId = null;
                  let lowestPrice = Infinity;

                  filteredCabs.forEach((cab) => {
                    const priceInfo = getCabPrice(cab);
                    const price = priceInfo?.originalPrice || cab.price || Infinity;
                    if (price < lowestPrice) {
                      lowestPrice = price;
                      bestValueCabId = cab._id;
                    }
                  });

                  return filteredCabs.map((cab) => {
                    const priceInfo = getCabPrice(cab);
                    const isBestValue = cab._id === bestValueCabId && filteredCabs.length > 1;

                    return (
                      <CabCard
                        key={cab._id}
                        cab={cab}
                        priceInfo={priceInfo}
                        onSelect={() => handleSelectCab(cab)}
                        isSelected={selectedCab && selectedCab._id === cab._id}
                        gstRate={gstRate}
                        isBestValue={isBestValue}
                        distance={distance}
                        serviceType={serviceType}
                      />
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Display error if any */}
      {error && (
        <div>
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded my-4"
            role="alert"
          >
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Style element for slide-up drawer animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      {/* Bottom Floating Filter Bar for Mobile */}
      <div className="md:hidden fixed left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 backdrop-blur-md text-white py-1.5 px-3 rounded-full shadow-[0_4px_16px_rgba(0,0,0,0.25)] border border-slate-700/50 flex items-center h-11 transition-all duration-300 bottom-6">
        <button 
          onClick={() => setShowCarModelDrawer(true)} 
          className="flex items-center gap-1.5 px-3.5 py-1 text-[11px] font-bold text-white tracking-wide active:scale-95 transition-all"
        >
          <FaChair className="text-sm text-blue-400" />
          <span>Car Model</span>
          {selectedModelTypes.length > 0 && (
            <span className="flex items-center justify-center bg-blue-500 text-white text-[9px] w-4.5 h-4.5 rounded-full font-black px-1 ml-0.5">
              {selectedModelTypes.length}
            </span>
          )}
        </button>
        
        <div className="h-4 w-[1px] bg-slate-700/80" />
        
        <button 
          onClick={() => setShowSortDrawer(true)} 
          className="flex items-center gap-1.5 px-3.5 py-1 text-[11px] font-bold text-white tracking-wide active:scale-95 transition-all"
        >
          <FaSort className="text-sm text-blue-400" />
          <span>Sort</span>
          {sortBy && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full inline-block ml-0.5" />}
        </button>
        
        <div className="h-4 w-[1px] bg-slate-700/80" />
        
        <button 
          onClick={() => setShowAllFiltersDrawer(true)} 
          className="flex items-center gap-1.5 px-3.5 py-1 text-[11px] font-bold text-white tracking-wide active:scale-95 transition-all"
        >
          <FaFilter className="text-sm text-blue-400" />
          <span>All Filters</span>
          {(selectedCabTypes.length > 0 || selectedFuelTypes.length > 0 || selectedModelTypes.length > 0) && (
            <span className="flex items-center justify-center bg-blue-500 text-white text-[9px] w-4.5 h-4.5 rounded-full font-black px-1 ml-0.5">
              {(selectedCabTypes.length > 0 ? 1 : 0) + (selectedFuelTypes.length > 0 ? 1 : 0) + (selectedModelTypes.length > 0 ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Car Model Bottom Sheet Drawer */}
      {showCarModelDrawer && (
        <div className="md:hidden fixed inset-0 z-[100] flex flex-col justify-end">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] transition-opacity" 
            onClick={() => setShowCarModelDrawer(false)}
          />
          <div className="relative bg-white rounded-t-[24px] shadow-2xl z-10 w-full max-h-[85vh] flex flex-col overflow-hidden animate-slide-up pb-6">
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto my-3 flex-shrink-0" />
            <div className="flex justify-between items-center px-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                <FaChair className="text-blue-600" /> Select Car Model
              </h3>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setSelectedModelTypes([])}
                  className="text-xs font-bold text-gray-500 hover:text-gray-900 active:scale-95 transition-all"
                >
                  Clear
                </button>
                <button 
                  onClick={() => setShowCarModelDrawer(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </div>
            
            <div className="p-5 overflow-y-auto max-h-[50vh] space-y-4">
              {uniqueModelTypes.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No models available</p>
              ) : (
                uniqueModelTypes.map((model) => {
                  const isChecked = selectedModelTypes.includes(model);
                  const count = cabOptions.filter(cab => cab.model === model || cab.name === model).length;
                  return (
                    <label 
                      key={model} 
                      className="flex items-center justify-between py-2 cursor-pointer select-none"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleFilter("modelType", model)}
                          className="w-5 h-5 accent-blue-600 rounded cursor-pointer"
                        />
                        <span className="text-sm font-semibold text-gray-800">{model}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">({count})</span>
                    </label>
                  );
                })
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
              <button
                onClick={() => setShowCarModelDrawer(false)}
                className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl active:scale-[0.98] transition-all shadow-md shadow-blue-200"
              >
                Apply Filters ({selectedModelTypes.length} Selected)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sort Bottom Sheet Drawer */}
      {showSortDrawer && (
        <div className="md:hidden fixed inset-0 z-[100] flex flex-col justify-end">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] transition-opacity" 
            onClick={() => setShowSortDrawer(false)}
          />
          <div className="relative bg-white rounded-t-[24px] shadow-2xl z-10 w-full max-h-[85vh] flex flex-col overflow-hidden animate-slide-up pb-6">
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto my-3 flex-shrink-0" />
            <div className="flex justify-between items-center px-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                <FaSort className="text-blue-600" /> Sort Cabs
              </h3>
              <button 
                onClick={() => setShowSortDrawer(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
              >
                <FaTimes size={12} />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              {[
                { label: "Recommended (Default)", value: "" },
                { label: "Price: Low to High", value: "price_low_high" },
                { label: "Price: High to Low", value: "price_high_low" }
              ].map((option) => {
                const isChecked = sortBy === option.value;
                return (
                  <label 
                    key={option.value} 
                    className="flex items-center justify-between py-2.5 cursor-pointer select-none"
                    onClick={() => {
                      setSortBy(option.value);
                      setShowSortDrawer(false);
                    }}
                  >
                    <span className={`text-sm font-semibold ${isChecked ? 'text-blue-600' : 'text-gray-800'}`}>
                      {option.label}
                    </span>
                    <input
                      type="radio"
                      checked={isChecked}
                      onChange={() => {
                        setSortBy(option.value);
                        setShowSortDrawer(false);
                      }}
                      className="w-5 h-5 accent-blue-600 cursor-pointer"
                    />
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* All Filters Bottom Sheet Drawer */}
      {showAllFiltersDrawer && (
        <div className="md:hidden fixed inset-0 z-[100] flex flex-col justify-end">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] transition-opacity" 
            onClick={() => setShowAllFiltersDrawer(false)}
          />
          <div className="relative bg-white rounded-t-[24px] shadow-2xl z-10 w-full max-h-[85vh] flex flex-col overflow-hidden animate-slide-up pb-6">
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto my-3 flex-shrink-0" />
            <div className="flex justify-between items-center px-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                <FaFilter className="text-blue-600" /> All Filters
              </h3>
              <div className="flex items-center gap-3">
                <button 
                  onClick={clearAllFilters}
                  className="text-xs font-bold text-gray-500 hover:text-gray-900 active:scale-95 transition-all"
                >
                  Clear All
                </button>
                <button 
                  onClick={() => setShowAllFiltersDrawer(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </div>
            
            <div className="p-5 overflow-y-auto max-h-[55vh] space-y-6">
              {/* Cab Type Section */}
              {uniqueCabTypes.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Cab Category</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {uniqueCabTypes.map((type) => {
                      const isActive = selectedCabTypes.includes(type);
                      const count = cabOptions.filter(cab => cab.category?.toLowerCase() === type.toLowerCase() || cab.name?.toLowerCase().includes(type.toLowerCase())).length;
                      return (
                        <button
                          key={type}
                          onClick={() => toggleFilter("cabType", type)}
                          className={`flex items-center justify-between px-3 py-2 rounded-xl border text-left text-xs font-bold transition-all ${
                            isActive
                              ? "bg-blue-50 border-blue-600 text-blue-600 shadow-sm"
                              : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          <span>{type}</span>
                          <span className="text-[10px] text-gray-400 font-semibold">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fuel Type Section */}
              {uniqueFuelTypes.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Fuel Type</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {uniqueFuelTypes.map((fuel) => {
                      const isActive = selectedFuelTypes.includes(fuel);
                      const count = cabOptions.filter(cab => cab.fuelType?.toLowerCase() === fuel.toLowerCase()).length;
                      return (
                        <button
                          key={fuel}
                          onClick={() => toggleFilter("fuelType", fuel)}
                          className={`flex items-center justify-between px-3 py-2 rounded-xl border text-left text-xs font-bold transition-all ${
                            isActive
                              ? "bg-blue-50 border-blue-600 text-blue-600 shadow-sm"
                              : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          <span>{fuel}</span>
                          <span className="text-[10px] text-gray-400 font-semibold">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Model Type Section */}
              {uniqueModelTypes.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Car Model</h4>
                  <div className="space-y-2">
                    {uniqueModelTypes.map((model) => {
                      const isChecked = selectedModelTypes.includes(model);
                      const count = cabOptions.filter(cab => cab.model === model || cab.name === model).length;
                      return (
                        <label 
                          key={model} 
                          className="flex items-center justify-between py-1.5 cursor-pointer select-none"
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleFilter("modelType", model)}
                              className="w-4 h-4 accent-blue-600 rounded cursor-pointer"
                            />
                            <span className="text-xs font-semibold text-gray-800">{model}</span>
                          </div>
                          <span className="text-[10px] font-bold text-gray-400">({count})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setShowAllFiltersDrawer(false)}
                className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl active:scale-[0.98] transition-all shadow-md"
              >
                View Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarSelection;
